import { useEffect, useState, useRef, Fragment, useCallback, type ChangeEvent, type KeyboardEvent } from 'react'
import { Layout, List, Avatar, Input, Button, Select, message, Badge, Spin, Modal, Radio, Popconfirm, Space, Tooltip, Segmented, Typography, Empty, Tag, Image, type RadioChangeEvent } from 'antd'
import { SendOutlined, UserOutlined, ReloadOutlined, PlusOutlined, UserAddOutlined, DeleteOutlined, SmileOutlined, SearchOutlined, DownOutlined, FileExcelOutlined, TeamOutlined, AppstoreOutlined, PaperClipOutlined, InboxOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons'
import api, { accountsApi, contactsApi, chatsApi, internalChatsApi } from '../services/api'
import { safeText } from '../utils/safeText'
import { useAuthStore } from '../store/authStore'
import { useSearchParams } from 'react-router-dom'

const { Content } = Layout
const { TextArea } = Input

interface Dialog {
    chat_id: number
    tg_chat_id?: number
    id?: number
    db_chat_id: number | null
    username?: string | null
    first_name?: string | null
    last_name?: string | null
    type: string
    unread_count: number
    last_message?: string
    last_message_date?: string
    message_id?: number
    photo?: string
    contact_status?: string
    is_deleted?: boolean
    is_account_blocked?: boolean
    is_blocked?: boolean
    read_only?: boolean
    tg_account_id?: number
    account_phone?: string
    account_username?: string
    contact_name?: string
    contact_username?: string
    contact_photo_url?: string
    photo_url?: string
    telegram_peer_id?: number
    peer_type?: 'user' | 'chat' | 'channel'
    chat_type?: 'private' | 'bot' | 'group' | 'supergroup' | 'forum' | 'channel'
    title?: string | null
    is_bot?: boolean
    is_forum?: boolean
    can_send_messages?: boolean
}

interface DialogCursor {
    offset_date?: string | number
    offset_id: number
    offset_peer_id: number
}

interface Message {
    id: number
    tg_message_id?: number
    text: string
    date: string
    outgoing: boolean
    from_user: { id: number; first_name: string; username?: string } | null
    is_deleted_in_telegram?: boolean
    operator_id?: number
    operator_name?: string
    is_read?: boolean
    is_edited?: boolean
    reply_to?: number
    content_type?: string
    media_file_name?: string
    media_file_path?: string
    media_mime_type?: string
    forward_from_name?: string
    forward_from_id?: number
    topic_id?: number
    sender_peer_type?: string
    sender_peer_id?: number
    sender_name?: string
    sender_username?: string
}

interface ForumTopic {
    id: number
    title: string
    icon_color?: number
    icon_emoji_id?: number
    is_general?: boolean
    is_closed?: boolean
    is_hidden?: boolean
    is_pinned?: boolean
    pinned_order?: number
    last_message?: string
    last_message_date?: string
    unread_count: number
}

interface Account {
    id: number
    phone: string
    username: string | null
    first_name: string | null
    status: string
    is_connected?: boolean
}

type AccountStatusFilter = 'all' | 'active' | 'banned' | 'unavailable'

interface InternalChat {
    id: number
    other_user: {
        id: number
        username: string | null
        full_name: string | null
        avatar_url: string | null
        role: string
    }
    last_message: {
        id: number
        text_content: string
        created_at: string
        is_read: boolean
        sender_id: number
    } | null
    unread_count: number
}

interface InternalMessage {
    id: number
    sender_id: number
    text_content: string
    created_at: string
    is_read: boolean
}

const EMOJI_CATEGORIES = [
    {
        name: '😀 Смайлы',
        emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','😎','🤓','🧐'],
    },
    {
        name: '❤️ Жесты и сердца',
        emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✌️','🤞','🤟','🤘','👍','👎','👊','✊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🧠','🦷','👀','👁️','👅','👄'],
    },
    {
        name: '🐶 Животные',
        emojis: ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜','🦟','🦗','🐢','🐍','🦎','🦖','🦕','🐙','🦑','🦐','🦀','🐡','🐠','🐟','🐬','🐳','🦈','🐊','🐅','🐆','🦓'],
    },
    {
        name: '🍕 Еда и напитки',
        emojis: ['🍎','🍊','🍋','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🥑','🍆','🥕','🌽','🌶️','🥦','🧅','🧄','🥔','🍠','🥐','🥖','🍞','🥨','🥯','🧀','🥚','🍳','🥞','🧇','🥓','🥩','🍗','🍖','🌮','🌯','🥙','🧆','🥚','🍜','🍝','🍲','🍛','🍣','🍱','🥟','🍤','🍙','🍚','🍘','🍥','🍦','🍧','🍨','🍩','🍪','🎂','🍰','🧁','🍫','🍬','🍭','🍮','🍯','☕','🧃','🥤','🧋'],
    },
    {
        name: '⚽ Спорт и активности',
        emojis: ['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🎱','🏓','🏸','🥊','🥋','🎯','🥅','⛳','🎣','🎿','🛷','🥌','🎽','🎿','🛹','🛺','🛼','🤸','⛹️','🏋️','🤼','🤺','🏇','🚵','🧘','🤾','🏌️','🧗','🏊','🚣','🧜','🧚','🦸','🦹'],
    },
    {
        name: '✈️ Путешествия',
        emojis: ['🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','🛵','🏍️','🚲','🛴','🛹','🚁','🛸','🚀','✈️','🛩️','🛫','🛬','💺','🪂','🚢','⛴️','🛥️','🚤','⛵','🏄','🤿','🗺️','🧭','🏔️','⛰️','🌋','🗻','🏕️','🏖️','🏗️','🏘️','🏚️','🏠','🏡','🏢','🏣','🏤','🏥','🏦','🏨','🏩','🏪','🗼','🗽','⛪','🕌','🕍'],
    },
    {
        name: '💡 Предметы',
        emojis: ['⌚','📱','💻','⌨️','🖥️','🖨️','🖱️','🖲️','💾','💿','📀','📷','📸','📹','🎥','📽️','🎞️','📞','☎️','📟','📠','📺','📻','🧭','⏱️','⏰','🕰️','⌛','📡','🔋','🔌','💡','🔦','🕯️','🪔','🧰','🔧','🔨','⚒️','🛠️','🔩','🗜️','🔗','⛓️','🧲','🔑','🗝️','🔐','🔒','🔓','📦','📫','📪','📬','📭','📮','📯'],
    },
    {
        name: '💥 Символы',
        emojis: ['✅','❌','❓','❗','💯','🔥','⭐','✨','🎉','🎊','🎈','🎁','🎀','🏆','🥇','🥈','🥉','🎖️','🏅','🎗️','🎫','🎟️','🎪','🤹','🎭','🎨','🎬','🎤','🎧','🎼','🎹','🥁','🎷','🎺','🎸','🎻','🪕','🎮','🕹️','🃏','🀄','🎲','🧩','🎯','♟️','🎳','🎰','🧨','✏️','📝','📒','📔','📕','📖','📗','📘','📙','📚','📓'],
    },
]

// Keyword map for emoji text search (Russian + English)
const EMOJI_KEYWORDS: Record<string, string> = {
    '😀': 'smile улыбка', '😃': 'smile улыбка', '😄': 'smile улыбка радость', '😁': 'grin ухмылка',
    '😆': 'laugh смех', '😅': 'sweat пот нервно', '🤣': 'rofl ржу смех', '😂': 'joy слезы смех плачу',
    '🙂': 'smile улыбка', '🙃': 'перевернутый', '😉': 'wink подмигивание', '😊': 'blush стеснение',
    '😇': 'angel ангел', '🥰': 'love любовь сердечки', '😍': 'love любовь глаза сердце heart',
    '🤩': 'wow звезды круто', '😘': 'kiss поцелуй', '😗': 'kiss поцелуй', '😚': 'kiss поцелуй',
    '😙': 'kiss свист', '😋': 'yummy вкусно язык', '😛': 'tongue язык', '😜': 'wink язык',
    '🤪': 'crazy безумный', '😝': 'tongue язык', '🤑': 'money деньги доллар',
    '🤗': 'hug обнимашки', '🤭': 'oops тихо', '🤫': 'shush тихо секрет', '🤔': 'think думаю hmm',
    '🤐': 'zip молчание', '🤨': 'suspicious подозрение', '😐': 'neutral нейтрально',
    '😑': 'expressionless безразличие', '😶': 'silence тишина', '😏': 'smirk усмешка',
    '😒': 'unamused недовольство', '🙄': 'eyes глаза закатить', '😬': 'grimace гримаса',
    '🤥': 'lie ложь', '😌': 'relieved облегчение', '😔': 'sad грусть печаль',
    '😪': 'sleepy сонный', '🤤': 'drool слюни', '😴': 'sleep сон спать zzz',
    '😷': 'mask маска болезнь', '🤒': 'sick болезнь температура', '🤕': 'hurt ранение',
    '🤢': 'nauseated тошнота', '🤧': 'sneeze чихание', '🥵': 'hot жарко', '🥶': 'cold холодно',
    '🥴': 'drunk пьяный', '😵': 'dizzy головокружение', '🤯': 'mind blown взрыв мозга',
    '🤠': 'cowboy ковбой', '🥳': 'party вечеринка праздник', '😎': 'cool круто очки',
    '🤓': 'nerd ботан очки', '🧐': 'monocle монокль',
    '❤️': 'heart сердце love красный', '🧡': 'heart сердце оранжевый', '💛': 'heart сердце желтый',
    '💚': 'heart сердце зеленый', '💙': 'heart сердце синий', '💜': 'heart сердце фиолетовый',
    '🖤': 'heart сердце черный', '💔': 'broken разбитое сердце',
    '👍': 'like лайк хорошо палец вверх ok класс', '👎': 'dislike дизлайк плохо палец вниз',
    '👏': 'clap хлопать аплодисменты bravo', '🤝': 'handshake рукопожатие сделка',
    '🙏': 'pray молитва пожалуйста спасибо', '💪': 'muscle мышцы сила бицепс strong',
    '👀': 'eyes глаза смотреть',
    '🔥': 'fire огонь hot жара пламя', '⭐': 'star звезда', '✨': 'sparkles блеск искры',
    '🎉': 'party tada ура congratulations поздравление', '🎁': 'gift подарок present',
    '🏆': 'trophy трофей кубок winner', '✅': 'check галочка yes да готово done',
    '❌': 'cross крестик нет no отмена', '❓': 'question вопрос', '❗': 'exclamation восклицание',
    '💯': 'hundred сто процентов perfect', '🚀': 'rocket ракета запуск launch',
    '☕': 'coffee кофе чай', '📱': 'phone телефон mobile', '💻': 'computer компьютер laptop',
    '🎮': 'game игра', '🔑': 'key ключ', '🔒': 'lock замок', '📦': 'package посылка коробка',
}

function searchEmojis(query: string): string[] {
    if (!query) return []
    const q = query.toLowerCase()
    const results: string[] = []
    for (const [emoji, keywords] of Object.entries(EMOJI_KEYWORDS)) {
        if (keywords.toLowerCase().includes(q)) results.push(emoji)
    }
    for (const cat of EMOJI_CATEGORIES) {
        if (cat.name.toLowerCase().includes(q)) {
            for (const e of cat.emojis) {
                if (!results.includes(e)) results.push(e)
            }
        }
    }
    return results
}

function OnDemandVideo({ src, round = false, label = 'Видео' }: { src: string; round?: boolean; label?: string }) {
    const [loaded, setLoaded] = useState(false)
    if (!loaded) {
        return (
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation()
                    setLoaded(true)
                }}
                style={{
                    width: round ? 180 : 280,
                    maxWidth: '100%',
                    height: round ? 180 : 170,
                    borderRadius: round ? '50%' : 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(0,0,0,0.35)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                }}
            >
                <span style={{ fontSize: 34 }}>▶</span>
                <span style={{ fontSize: 13 }}>Загрузить {label.toLowerCase()}</span>
            </button>
        )
    }
    return (
        <video
            src={src}
            controls
            autoPlay
            preload="metadata"
            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: round ? '50%' : 8, display: 'block' }}
        />
    )
}


function Chats() {
    const { user } = useAuthStore()
    const [searchParams] = useSearchParams()
    const [activeTab, setActiveTab] = useState<'telegram' | 'internal'>(() => {
        const tabParam = searchParams.get('tab')
        if (tabParam === 'internal') return 'internal'
        return (localStorage.getItem('chatsActiveTab') as 'telegram' | 'internal') || 'telegram'
    })

    const [accounts, setAccounts] = useState<Account[]>([])
    const [accountsLoaded, setAccountsLoaded] = useState(false)
    const [accountStatusFilter, setAccountStatusFilter] = useState<AccountStatusFilter>(() => {
        const saved = localStorage.getItem('accountStatusFilter')
        return saved === 'active' || saved === 'banned' || saved === 'unavailable' ? saved : 'all'
    })
    const [selectedAccount, setSelectedAccount] = useState<number | null>(() => {
        return Number(localStorage.getItem('selectedAccount')) || null
    })
    const [dialogs, setDialogs] = useState<Dialog[]>([])
    const [selectedDialog, setSelectedDialog] = useState<Dialog | null>(null)
    const [topics, setTopics] = useState<ForumTopic[]>([])
    const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>(undefined)
    const [loadingTopics, setLoadingTopics] = useState(false)
    const [messages, setMessages] = useState<Message[]>([])
    const [hasMoreMessages, setHasMoreMessages] = useState(true)
    const loadingMoreRef = useRef(false)
    
    // Dialog pagination
    const [hasMoreDialogs, setHasMoreDialogs] = useState(true)
    const [loadingMoreDialogs, setLoadingMoreDialogs] = useState(false)
    const [dialogCursor, setDialogCursor] = useState<DialogCursor | null>(null)
    const loadingDialogsRef = useRef(false)
    
    // File upload state
    const [mediaFile, setMediaFile] = useState<File | null>(null)
    const [mediaCaption, setMediaCaption] = useState("")
    const [isDragging, setIsDragging] = useState(false)
    const [uploadingMedia, setUploadingMedia] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [internalChats, setInternalChats] = useState<InternalChat[]>([])
    const [selectedInternalChat, setSelectedInternalChat] = useState<InternalChat | null>(null)
    const [internalMessages, setInternalMessages] = useState<InternalMessage[]>([])
    const [loadingInternalChats, setLoadingInternalChats] = useState(false)
    const [loadingInternalMessages, setLoadingInternalMessages] = useState(false)

    const [newMessage, setNewMessage] = useState('')
    const [editMessageId, setEditMessageId] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [sending, setSending] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [showScrollButton, setShowScrollButton] = useState(false)

    const internalPollingRef = useRef<any>(null)
    const [siderWidth, setSiderWidth] = useState(350)
    const [isResizing, setIsResizing] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const pendingScrollMessageIdRef = useRef<number | null>(null)
    const openedChatFromUrlRef = useRef<string | null>(null)

    const [newChatModalOpen, setNewChatModalOpen] = useState(false)
    const [newChatType, setNewChatType] = useState<'username' | 'phone'>('username')
    const [newChatTarget, setNewChatTarget] = useState('')
    const [newChatMessage, setNewChatMessage] = useState('')
    const [sendingNewChat, setSendingNewChat] = useState(false)
    const [searchResults, setSearchResults] = useState<any[]>([])
    const [selectedSearchResult, setSelectedSearchResult] = useState<any>(null)
    const [searchingUser, setSearchingUser] = useState(false)

    // Закреплённые сообщения
    const [pinnedMessages, setPinnedMessages] = useState<any[]>([])
    const [currentPinnedIndex, setCurrentPinnedIndex] = useState(0)
    const [showPinnedBanner, setShowPinnedBanner] = useState(true)
    const [deleteChatModalOpen, setDeleteChatModalOpen] = useState(false)
    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, messageId: number, isPinned?: boolean, msgText?: string, msgOutgoing?: boolean } | null>(null)

    // Reply (ответ на сообщение)
    const [replyTo, setReplyTo] = useState<{ id: number, text: string } | null>(null)

    // Forward (пересылка)
    const [forwardModalOpen, setForwardModalOpen] = useState(false)
    const [forwardMsgIds, setForwardMsgIds] = useState<number[]>([])
    const [forwardTargetChatIds, setForwardTargetChatIds] = useState<number[]>([])
    const [forwardSearchQuery, setForwardSearchQuery] = useState('')

    // Select mode (мультивыбор)
    const [isSelectMode, setIsSelectMode] = useState(false)
    const [selectedMsgIds, setSelectedMsgIds] = useState<number[]>([])

    // Папки Telegram
    const [folders, setFolders] = useState<{ id: number, title: string, emoticon?: string }[]>([])
    const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined)

    const [operatorsMap, setOperatorsMap] = useState<Record<number, { name: string, avatar?: string }>>({})
    const [loadingExport, setLoadingExport] = useState(false)

    // Message cache: `${accountId}_${chatId}` -> Message[]
    const messageCache = useRef<Record<string, Message[]>>({})

    // Emoji picker
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const emojiPickerRef = useRef<HTMLDivElement>(null)
    const emojiButtonRef = useRef<HTMLButtonElement>(null)
    const [emojiPos, setEmojiPos] = useState<{ top: number; left: number } | null>(null)
    const [emojiSearch, setEmojiSearch] = useState('')
    const [recentEmojis, setRecentEmojis] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem('recentEmojis') || '[]') } catch { return [] }
    })

    const handleEmojiSelect = (emoji: string) => {
        setNewMessage(prev => prev + emoji)
        setShowEmojiPicker(false)
        setEmojiSearch('')
        // Save to recent (max 32, no duplicates)
        setRecentEmojis(prev => {
            const updated = [emoji, ...prev.filter(e => e !== emoji)].slice(0, 32)
            localStorage.setItem('recentEmojis', JSON.stringify(updated))
            return updated
        })
    }

    const handleToggleEmoji = () => {
        if (!showEmojiPicker && emojiButtonRef.current) {
            const rect = emojiButtonRef.current.getBoundingClientRect()
            setEmojiPos({
                top: rect.top - 8,
                left: rect.left,
            })
        }
        if (showEmojiPicker) setEmojiSearch('')
        setShowEmojiPicker(v => !v)
    }

    // Close emoji picker when clicking outside
    useEffect(() => {
        if (!showEmojiPicker) return
        const handleClickOutside = (e: MouseEvent) => {
            if (
                emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node) &&
                emojiButtonRef.current && !emojiButtonRef.current.contains(e.target as Node)
            ) {
                setShowEmojiPicker(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showEmojiPicker])

    // Debounced search
    const searchTimerRef = useRef<any>(null)
    const [rawSearch, setRawSearch] = useState('')
    const handleSearchChange = (val: string) => {
        setRawSearch(val)
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => setSearchQuery(val), 200)
    }

    useEffect(() => {
        const fetchOperators = async () => {
            try {
                const res = await api.get('/operators')
                const map: Record<number, { name: string, avatar?: string }> = {}
                res.data.forEach((op: any) => {
                    map[op.id] = { name: op.username, avatar: op.avatar_url }
                })
                setOperatorsMap(map)
            } catch (e) {
                console.error("Failed to fetch operators", e)
            }
        }
        fetchOperators()
    }, [])

    const handleSwitchAccount = async (targetAccountId: number) => {
        if (!selectedDialog) return
        try {
            const contactIdentifier = selectedDialog.username || selectedDialog.contact_username
            if (!contactIdentifier) {
                message.error("Не удалось определить контакт для переключения")
                return
            }

            setLoading(true)
            selectedAccountRef.current = targetAccountId
            selectedDialogRef.current = null
            selectedTopicIdRef.current = undefined
            dialogSelectionGenerationRef.current += 1
            messageRequestGenerationRef.current += 1
            setSelectedAccount(targetAccountId)
            localStorage.setItem('selectedAccount', String(targetAccountId))

            const res = await accountsApi.getDialogs(targetAccountId)
            const accountDialogs = res.data.dialogs

            const existing = accountDialogs.find((d: any) =>
                (d.username && d.username === contactIdentifier) ||
                (d.contact_username && d.contact_username === contactIdentifier)
            )

            if (existing) {
                await handleSelectDialog({ ...existing, chat_id: existing.id })
            } else {
                message.info("Чат с этим контактом не найден на выбранном аккаунте. Создаем новый...")
                await accountsApi.sendToNewContact(targetAccountId, contactIdentifier, undefined, "Chat switch check")
                setSelectedDialog(null)
            }
        } catch (e) {
            console.error(e)
            message.error("Ошибка переключения аккаунта")
        } finally {
            setLoading(false)
        }
    }

    const getOperatorInfo = (id?: number) => {
        if (!id) return null
        return operatorsMap[id]
    }

    const exportChats = async () => {
        setLoadingExport(true)
        try {
            message.info('Начинаем экспорт, пожалуйста подождите...')

            // Collect IDs of currently displayed chats if we aren't fetching all
            // For now let's just export all for the selected account or globally
            const res = await chatsApi.exportExcel(true)

            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url

            const contentDisposition = res.headers['content-disposition']
            let fileName = 'chats_export.xlsx'
            if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
                const matches = /filename="?([^"]+)"?/.exec(contentDisposition)
                if (matches != null && matches[1]) fileName = matches[1]
            }

            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            message.success('Экспорт завершен')
        } catch (e: any) {
            console.error(e)
            message.error(e.response?.data?.detail || 'Ошибка экспорта')
        } finally {
            setLoadingExport(false)
        }
    }

    useEffect(() => {
        const tab = searchParams.get('tab')
        const chatParam = searchParams.get('chat')
        const accountParam = searchParams.get('account')
        const messageParam = searchParams.get('messageId')

        if (tab === 'internal') {
            setActiveTab('internal')
            const chatId = searchParams.get('chatId')
            if (chatId) {
                internalChatsApi.getMyChats().then(res => {
                    const chats = res.data
                    setInternalChats(chats)
                    const chatToSelect = chats.find((c: InternalChat) => c.id === Number(chatId))
                    if (chatToSelect) handleSelectInternalChat(chatToSelect)
                }).catch(console.error)
            }
            return
        }

        if (!chatParam) return

        const targetChatId = Number(chatParam)
        if (Number.isNaN(targetChatId)) return

        const targetAccountId = accountParam ? Number(accountParam) : null
        const targetMessageId = messageParam ? Number(messageParam) : null
        const validTargetMessageId = targetMessageId && !Number.isNaN(targetMessageId) ? targetMessageId : undefined
        const topicParam = searchParams.get('topicId')
        const targetTopicId = topicParam ? Number(topicParam) : undefined
        const validTargetTopicId = targetTopicId && !Number.isNaN(targetTopicId) ? targetTopicId : undefined
        const navigationKey = `telegram:${targetAccountId || selectedAccount || ''}:${targetChatId}:${validTargetTopicId || ''}:${validTargetMessageId || ''}`
        if (openedChatFromUrlRef.current === navigationKey) return

        setActiveTab('telegram')

        if (selectedFolderId !== undefined) {
            setSelectedFolderId(undefined)
            return
        }

        if (targetAccountId && !Number.isNaN(targetAccountId) && selectedAccount !== targetAccountId) {
            setDialogs([])
            setSelectedDialog(null)
            setSelectedAccount(targetAccountId)
            localStorage.setItem('selectedAccount', String(targetAccountId))
            return
        }

        if (!dialogs.length) return

        const dialogToSelect = dialogs.find((dialog) =>
            Number(dialog.chat_id) === targetChatId ||
            Number(dialog.tg_chat_id) === targetChatId ||
            Number(dialog.id) === targetChatId
        )

        if (dialogToSelect) {
            openedChatFromUrlRef.current = navigationKey
            handleSelectDialog(dialogToSelect, validTargetMessageId, validTargetTopicId)
        }
    }, [searchParams, selectedAccount, selectedFolderId, dialogs])

    useEffect(() => {
        if (activeTab === 'telegram') {
            fetchAccounts()
        } else {
            if (!searchParams.get('chatId')) fetchInternalChats()
        }
    }, [activeTab])

    useEffect(() => {
        if (activeTab === 'internal') {
            internalPollingRef.current = setInterval(() => {
                internalChatsApi.getMyChats().then((res: any) => {
                    setInternalChats((prev: InternalChat[]) => {
                        const isDifferent = JSON.stringify(prev) !== JSON.stringify(res.data)
                        return isDifferent ? res.data : prev
                    })
                }).catch(() => { })

                if (selectedInternalChat) {
                    internalChatsApi.getMessages(selectedInternalChat.id, { limit: 50 }).then((res: any) => {
                        const freshMessages = res.data.reverse()
                        setInternalMessages((prevMsg: InternalMessage[]) => {
                            if (freshMessages.length > prevMsg.length || (freshMessages.length > 0 && prevMsg.length > 0 && freshMessages[freshMessages.length - 1].id !== prevMsg[prevMsg.length - 1].id)) {
                                return freshMessages
                            }
                            return prevMsg
                        })
                    }).catch(() => { })
                }
            }, 3000)
        }
        return () => {
            if (internalPollingRef.current) clearInterval(internalPollingRef.current)
        }
    }, [activeTab, selectedInternalChat?.id])

    const fetchInternalChats = async () => {
        setLoadingInternalChats(true)
        try {
            const response = await internalChatsApi.getMyChats()
            setInternalChats(response.data)
        } catch (error) {
            console.error('Failed to load internal chats', error)
        } finally {
            setLoadingInternalChats(false)
        }
    }

    const fetchInternalMessages = async (chatId: number) => {
        setLoadingInternalMessages(true)
        try {
            const response = await internalChatsApi.getMessages(chatId, { limit: 50 })
            setInternalMessages(response.data.reverse())
        } catch (error) {
            console.error('Failed to load internal messages', error)
        } finally {
            setLoadingInternalMessages(false)
        }
    }

    const handleSelectInternalChat = async (chat: InternalChat) => {
        setSelectedInternalChat(chat)
        await fetchInternalMessages(chat.id)
        setInternalChats((prev: InternalChat[]) => prev.map((c: InternalChat) =>
            c.id === chat.id ? { ...c, unread_count: 0 } : c
        ))
    }

    const handleSendInternalMessage = async () => {
        if (!selectedInternalChat || !newMessage.trim()) return
        setSending(true)
        try {
            const res = await internalChatsApi.sendMessage({
                recipient_id: selectedInternalChat.other_user.id,
                text_content: newMessage
            })
            const newMsg: InternalMessage = {
                ...res.data,
                created_at: new Date().toISOString()
            }
            setInternalMessages(prev => [...prev, newMsg])
            setNewMessage('')
            scrollToBottom()
        } catch (error) {
            message.error('Ошибка отправки сообщения')
        } finally {
            setSending(false)
        }
    }

    const fetchAccounts = async () => {
        try {
            const response = await accountsApi.getAll()
            const availableAccounts = response.data
                .filter((account: Account) => ['active', 'banned', 'inactive', 'limited'].includes(account.status))
                .sort((left: Account, right: Account) =>
                Number(right.status === 'active') - Number(left.status === 'active') || left.id - right.id
                )
            setAccounts(availableAccounts)
            setAccountsLoaded(true)
            if (availableAccounts.length > 0) {
                const urlAccountId = searchParams.get('account')
                const accountFromUrl = urlAccountId ? Number(urlAccountId) : null
                if (accountFromUrl && !Number.isNaN(accountFromUrl)) {
                    const urlAccount = availableAccounts.find((a: any) => a.id === accountFromUrl)
                    if (urlAccount) {
                        setSelectedAccount(urlAccount.id)
                        localStorage.setItem('selectedAccount', String(urlAccount.id))
                        return
                    }
                }

                const savedAccountId = localStorage.getItem('selectedAccount')
                if (savedAccountId === '-1') {
                    setSelectedAccount(-1)
                } else {
                    const accountToSelect = savedAccountId
                        ? availableAccounts.find((a: any) => a.id === Number(savedAccountId))
                        : null
                    if (accountToSelect) {
                        setSelectedAccount(accountToSelect.id)
                    } else {
                        setSelectedAccount(availableAccounts[0].id)
                        localStorage.setItem('selectedAccount', String(availableAccounts[0].id))
                    }
                }
            }
        } catch {
            message.error('Ошибка загрузки аккаунтов')
        }
    }

    const handleSelectAccount = (value: number) => {
        selectedAccountRef.current = value
        selectedDialogRef.current = null
        selectedTopicIdRef.current = undefined
        dialogSelectionGenerationRef.current += 1
        messageRequestGenerationRef.current += 1
        setSelectedAccount(value)
        setSelectedDialog(null)
        setMessages([])
        setPinnedMessages([])
        setNewChatModalOpen(false)
        setTopics([])
        setSelectedTopicId(undefined)
        localStorage.setItem('selectedAccount', String(value))
    }

    const accountMatchesStatusFilter = (account: Account, filter: AccountStatusFilter) =>
        filter === 'all'
        || account.status === filter
        || (filter === 'unavailable' && ['inactive', 'limited'].includes(account.status))

    const visibleAccounts = accounts.filter(account => accountMatchesStatusFilter(account, accountStatusFilter))

    const handleAccountStatusFilter = (value: AccountStatusFilter) => {
        setAccountStatusFilter(value)
        localStorage.setItem('accountStatusFilter', value)

        const filteredAccounts = accounts.filter(account => accountMatchesStatusFilter(account, value))
        if (selectedAccount && !filteredAccounts.some(account => account.id === selectedAccount)) {
            const nextAccount = filteredAccounts[0]
            if (nextAccount) {
                handleSelectAccount(nextAccount.id)
            } else {
                setSelectedAccount(null)
                setSelectedDialog(null)
                setTopics([])
                setSelectedTopicId(undefined)
                localStorage.removeItem('selectedAccount')
            }
        }
    }

    useEffect(() => {
        if (!accountsLoaded) return
        const filteredAccounts = accounts.filter(account =>
            accountMatchesStatusFilter(account, accountStatusFilter)
        )
        if (selectedAccount && filteredAccounts.some(account => account.id === selectedAccount)) {
            return
        }

        const nextAccount = filteredAccounts[0]
        if (nextAccount) {
            setSelectedAccount(nextAccount.id)
            setSelectedDialog(null)
            setTopics([])
            setSelectedTopicId(undefined)
            localStorage.setItem('selectedAccount', String(nextAccount.id))
        } else if (selectedAccount !== null) {
            setSelectedAccount(null)
            setSelectedDialog(null)
            setTopics([])
            setSelectedTopicId(undefined)
            localStorage.removeItem('selectedAccount')
        }
    }, [accounts, accountsLoaded, accountStatusFilter, selectedAccount])

    const selectedAccountRef = useRef<number | null>(selectedAccount)
    const selectedDialogRef = useRef<Dialog | null>(null)
    const selectedTopicIdRef = useRef<number | undefined>(undefined)
    const dialogSelectionGenerationRef = useRef(0)
    const messageRequestGenerationRef = useRef(0)
    const avatarSyncInFlightRef = useRef<Set<string>>(new Set())
    useEffect(() => {
        if (selectedAccountRef.current === selectedAccount) return
        selectedAccountRef.current = selectedAccount
        dialogSelectionGenerationRef.current += 1
        messageRequestGenerationRef.current += 1
    }, [selectedAccount])
    useEffect(() => {
        selectedDialogRef.current = selectedDialog
    }, [selectedDialog])
    useEffect(() => {
        selectedTopicIdRef.current = selectedTopicId
    }, [selectedTopicId])

    const isSelectedConversation = useCallback((accountId: number, chatId: number, topicId?: number) => {
        const currentDialog = selectedDialogRef.current
        if (!currentDialog) return false

        const currentAccountId = selectedAccountRef.current === -1
            ? currentDialog.tg_account_id
            : selectedAccountRef.current
        const dialogChatIds = [currentDialog.chat_id, currentDialog.tg_chat_id, currentDialog.id]

        return Number(currentAccountId) === Number(accountId)
            && dialogChatIds.some(id => id !== undefined && Number(id) === Number(chatId))
            && (selectedTopicIdRef.current ?? undefined) === (topicId ?? undefined)
    }, [])

    const removeMessagesFromLocalState = useCallback((
        accountId: number,
        chatIds: Array<number | null | undefined>,
        messageIds: number[],
        removeFromVisibleMessages: boolean,
    ) => {
        const deletedIds = new Set(messageIds.map(Number))
        if (removeFromVisibleMessages) {
            setMessages(previous => previous.filter(item => !deletedIds.has(Number(item.tg_message_id || item.id))))
        }

        const cachePrefixes = chatIds
            .filter((id): id is number => id !== null && id !== undefined)
            .map(id => `${accountId}_${id}_`)
        Object.keys(messageCache.current).forEach(cacheKey => {
            if (!cachePrefixes.some(prefix => cacheKey.startsWith(prefix))) return
            messageCache.current[cacheKey] = messageCache.current[cacheKey]
                .filter(item => !deletedIds.has(Number(item.tg_message_id || item.id)))
        })
    }, [])

    // Reconcile the active chat if a Telegram/WebSocket update was missed.
    useEffect(() => {
        if (!selectedDialog || !selectedAccount) return;
        if (selectedAccount !== -1 && accounts.find(account => account.id === selectedAccount)?.status !== 'active') return;
        const effectiveAccountId = selectedAccount === -1 ? selectedDialog.tg_account_id : selectedAccount
        if (!effectiveAccountId) return
        
        const syncActiveChat = async () => {
            try {
                const requestedChatId = selectedDialog.chat_id
                const requestedTopicId = selectedTopicIdRef.current
                const response = await accountsApi.getMessages(effectiveAccountId, requestedChatId, 50, 0, requestedTopicId)
                if (!isSelectedConversation(effectiveAccountId, requestedChatId, requestedTopicId)) return

                const refreshed: Message[] = (response.data.messages || []).map((item: any) => ({
                    ...item,
                    from_user: item.from_user || (item.sender_name ? {
                        id: item.sender_peer_id,
                        first_name: item.sender_name,
                        username: item.sender_username,
                    } : null),
                }))
                setMessages(previous => {
                    const merged = new Map(previous.map(item => [String(item.id), item]))
                    refreshed.forEach(item => merged.set(String(item.id), item))
                    const next = Array.from(merged.values()).sort((left, right) => {
                        const leftTime = left.date ? new Date(left.date).getTime() : 0
                        const rightTime = right.date ? new Date(right.date).getTime() : 0
                        return leftTime - rightTime || Number(left.id) - Number(right.id)
                    })
                    messageCache.current[`${effectiveAccountId}_${requestedChatId}_${requestedTopicId ?? 'all'}`] = next
                    return next
                })

                const latest = refreshed[refreshed.length - 1]
                if (latest) {
                    setDialogs(previous => previous.map(item => item.chat_id === requestedChatId ? {
                        ...item,
                        last_message: latest.text || (latest.content_type && latest.content_type !== 'text' ? '📎 Медиа' : ''),
                        last_message_date: latest.date,
                        unread_count: 0,
                        read_only: Boolean(response.data.read_only),
                    } : item))
                }
                const readOnly = Boolean(response.data.read_only)
                setSelectedDialog(previous => previous?.chat_id === requestedChatId && previous.read_only !== readOnly
                    ? { ...previous, read_only: readOnly }
                    : previous)
            } catch (error) {
                console.error("Silent sync failed", error);
            }
        };

        const intervalId = setInterval(syncActiveChat, 3000);
        return () => clearInterval(intervalId);
    }, [selectedDialog, selectedAccount, accounts]);

    useEffect(() => {
        if (selectedAccount) {
            setFolders([])
            setSelectedFolderId(undefined)
            setTopics([])
            setSelectedTopicId(undefined)
            setDialogCursor(null)
            if (selectedAccount !== -1) {
                accountsApi.getFolders(selectedAccount)
                    .then(response => setFolders(response.data?.folders || []))
                    .catch(() => setFolders([]))
            }
        }
    }, [selectedAccount])

    useEffect(() => {
        if (selectedAccount) {
            const token = localStorage.getItem('token')
            if (!token) return
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
            const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`
            
            let ws: WebSocket | null = null
            let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
            let heartbeatInterval: ReturnType<typeof setInterval> | null = null
            let reconnectDelay = 1000
            let isClosed = false

            const connect = () => {
                if (isClosed) return
                ws = new WebSocket(wsUrl)
                ws.onopen = () => {
                    reconnectDelay = 1000
                    if (heartbeatInterval) clearInterval(heartbeatInterval)
                    heartbeatInterval = setInterval(() => {
                        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }))
                    }, 25000)
                }
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data)
                        if (data.type === 'new_message') {
                            // Only add to Dialogs if it belongs to selectedAccount OR All Accounts (-1)
                            if (selectedAccount !== -1 && data.account_id && data.account_id !== selectedAccount) {
                                return;
                            }

                            const isMatch = (id: number | string) => String(id) === String(data.chat_id) || (data.db_chat_id && String(id) === String(data.db_chat_id));
                            const currentDialog = selectedDialogRef.current;
                            const messageTopicId = data.topic_id ?? data.message?.topic_id
                            const activeTopicId = selectedTopicIdRef.current
                            const isCurrentConversation = Boolean(
                                currentDialog &&
                                isMatch(currentDialog.chat_id) &&
                                (activeTopicId === undefined ? messageTopicId === undefined || messageTopicId === null : Number(messageTopicId) === Number(activeTopicId))
                            )

                            if (isCurrentConversation && currentDialog) {
                                setMessages(prev => {
                                    const existsIndex = prev.findIndex(m => String(m.id) === String(data.message.id))
                                    let updated = [...prev]
                                    const newMsg = {
                                        id: data.message.id,
                                        content_type: data.message.content_type,
                                        media_file_name: data.message.media_file_name,
                                        media_file_path: data.message.media_file_path,
                                        media_mime_type: data.message.media_mime_type,
                                        text: data.message.text,
                                        date: data.message.date,
                                        outgoing: data.message.outgoing,
                                        is_read: false,
                                        from_user: data.message.from_user,
                                        operator_id: data.message.operator_id,
                                        operator_name: data.message.operator_name,
                                        forward_from_name: data.message.forward_from_name,
                                        forward_from_id: data.message.forward_from_id,
                                        topic_id: messageTopicId,
                                        sender_peer_type: data.message.sender_peer_type,
                                        sender_peer_id: data.message.sender_peer_id,
                                        sender_name: data.message.sender_name,
                                        sender_username: data.message.sender_username,
                                    }
                                    if (existsIndex !== -1) {
                                        updated[existsIndex] = { ...updated[existsIndex], ...newMsg }
                                    } else {
                                        updated.push(newMsg)
                                    }
                                    const cacheKey = `${data.account_id}_${data.chat_id}_${messageTopicId ?? 'all'}`
                                    messageCache.current[cacheKey] = updated
                                    return updated
                                })
                            } else {
                                // Update cache for background chats if it exists
                                const cacheKey = `${data.account_id}_${data.chat_id}_${messageTopicId ?? 'all'}`
                                if (messageCache.current[cacheKey]) {
                                    const prev = messageCache.current[cacheKey]
                                    const existsIndex = prev.findIndex(m => String(m.id) === String(data.message.id))
                                    if (existsIndex !== -1) {
                                        prev[existsIndex] = { ...prev[existsIndex], ...data.message }
                                        messageCache.current[cacheKey] = [...prev]
                                    } else {
                                        messageCache.current[cacheKey] = [...prev, {
                                            id: data.message.id,
                                            content_type: data.message.content_type,
                                            media_file_name: data.message.media_file_name,
                                            media_file_path: data.message.media_file_path,
                                            media_mime_type: data.message.media_mime_type,
                                            text: data.message.text,
                                            date: data.message.date,
                                            outgoing: data.message.outgoing,
                                            is_read: false,
                                            from_user: data.message.from_user,
                                            operator_id: data.message.operator_id,
                                            operator_name: data.message.operator_name,
                                            forward_from_name: data.message.forward_from_name,
                                            forward_from_id: data.message.forward_from_id,
                                            topic_id: messageTopicId,
                                            sender_peer_type: data.message.sender_peer_type,
                                            sender_peer_id: data.message.sender_peer_id,
                                            sender_name: data.message.sender_name,
                                            sender_username: data.message.sender_username,
                                        }]
                                    }
                                }
                            }

                            setDialogs((prev: Dialog[]) => {
                                const updated = prev.map((d: Dialog) =>
                                    isMatch(d.chat_id)
                                        ? { ...d, last_message: data.message.text, last_message_date: data.message.date, unread_count: (currentDialog && isMatch(currentDialog.chat_id)) ? 0 : (d.unread_count || 0) + 1 }
                                        : d
                                )
                                // Move the updated dialog to the top
                                const idx = updated.findIndex((d: Dialog) => isMatch(d.chat_id))
                                if (idx > 0) {
                                    const [moved] = updated.splice(idx, 1)
                                    updated.unshift(moved)
                                } else if (idx === -1) {
                                    // Dialog not in list, refresh dialogs in the background
                                    fetchDialogs(false) // don't show loading spinner
                                }
                                return updated
                            })
                            if (messageTopicId !== undefined && messageTopicId !== null) {
                                setTopics(current => current.map(topic => Number(topic.id) === Number(messageTopicId)
                                    ? {
                                        ...topic,
                                        last_message: data.message.text || '📎 Медиа',
                                        last_message_date: data.message.date,
                                        unread_count: isCurrentConversation ? 0 : (topic.unread_count || 0) + (data.message.outgoing ? 0 : 1),
                                    }
                                    : topic
                                ))
                            }
                            if (isCurrentConversation && currentDialog) {
                                setMessages(prev => {
                                    // Дедупликация: пропускаем если сообщение с таким ID уже есть
                                    if (prev.some(m => m.id === data.message.id)) return prev
                                    // Дедупликация optimistic: если исходящее с тем же текстом добавлено недавно
                                    if (data.message.outgoing && data.message.text) {
                                        const now = Date.now()
                                        const hasDuplicate = prev.some(m =>
                                            m.outgoing && m.text === data.message.text &&
                                            Math.abs(now - new Date(m.date).getTime()) < 15000
                                        )
                                        if (hasDuplicate) return prev
                                    }
                                    return [...prev, {
                                        id: data.message.id,
                                        text: data.message.text,
                                        date: data.message.date,
                                        outgoing: data.message.outgoing || false,
                                        from_user: data.message.from_user || null,
                                        content_type: data.message.content_type,
                                        media_file_name: data.message.media_file_name,
                                        media_file_path: data.message.media_file_path,
                                        media_mime_type: data.message.media_mime_type,
                                        is_read: false,
                                        reply_to: data.message.reply_to_message_id,
                                        topic_id: messageTopicId,
                                        sender_peer_type: data.message.sender_peer_type,
                                        sender_peer_id: data.message.sender_peer_id,
                                        sender_name: data.message.sender_name,
                                        sender_username: data.message.sender_username,
                                    }]
                                })
                                
                                // Auto-refresh pinned messages if this is a pin action
                                if (data.message.content_type === 'action' && data.message.text?.includes('закреплено')) {
                                    accountsApi.getPinnedMessages(data.account_id || selectedAccount, currentDialog.chat_id || currentDialog.tg_chat_id || 0, activeTopicId).then(res => {
                                        setPinnedMessages(res.data.pinned_messages || [])
                                        setCurrentPinnedIndex(0)
                                        if (res.data.pinned_messages && res.data.pinned_messages.length > 0) setShowPinnedBanner(true)
                                    }).catch(e => console.error("Auto-refresh pinned messages failed:", e))
                                }
                            }
                        } else if (data.type === 'chat_migrated') {
                            if (selectedAccount !== -1 && data.account_id && data.account_id !== selectedAccount) return
                            const currentDialog = selectedDialogRef.current
                            const shouldSwitch = currentDialog && String(currentDialog.chat_id) === String(data.old_chat_id)
                            fetchDialogs(false).then(nextDialogs => {
                                if (!shouldSwitch || !nextDialogs) return
                                const migratedDialog = nextDialogs.find(item => String(item.chat_id) === String(data.new_chat_id))
                                if (migratedDialog) void handleSelectDialog(migratedDialog)
                            }).catch(error => console.error('Chat migration refresh failed:', error))
                        } else if (data.type === 'forum_topics_updated') {
                            const currentDialog = selectedDialogRef.current
                            const accountId = selectedAccount === -1 ? currentDialog?.tg_account_id : selectedAccount
                            if (currentDialog && accountId && String(currentDialog.chat_id) === String(data.chat_id)) {
                                accountsApi.getTopics(accountId, currentDialog.chat_id).then(response => {
                                    setTopics((response.data.topics || []).filter((item: ForumTopic) => !item.is_hidden))
                                }).catch(error => console.error('Topic refresh failed:', error))
                            }
                        } else if (data.type === 'edit_message') {
                            console.log("WS EDIT MESSAGE RECEIVED:", data);
                            if (selectedAccount !== -1 && data.account_id && data.account_id !== selectedAccount) {
                                return;
                            }
                            const isMatch = (id: number) => id === data.chat_id || (data.db_chat_id && id === data.db_chat_id);
                            const currentDialog = selectedDialogRef.current;

                            setDialogs((prev: Dialog[]) => {
                                return prev.map((d: Dialog) =>
                                    isMatch(d.chat_id)
                                        ? { ...d, last_message: data.message.text }
                                        : d
                                )
                            })
                            const editTopicId = data.topic_id ?? data.message?.topic_id
                            if (currentDialog && isMatch(currentDialog.chat_id) && (selectedTopicIdRef.current === undefined || Number(editTopicId) === Number(selectedTopicIdRef.current))) {
                                console.log("WS EDIT MESSAGE matches current dialog, updating messages array.");
                                setMessages(prev => {
                                    const next = prev.map(m => {
                                        if (m.tg_message_id === data.message.id || m.id === data.message.id) {
                                            console.log("WS EDIT MESSAGE found message to update:", m);
                                            return { ...m, text: data.message.text, is_edited: true };
                                        }
                                        return m;
                                    });
                                    return next;
                                });
                            }
                        } else if (data.type === 'delete_message') {
                            console.log("WS DELETE MESSAGE RECEIVED:", data);
                            if (selectedAccount !== -1 && data.account_id && data.account_id !== selectedAccount) {
                                return;
                            }
                            const isMatch = (id: number) => String(id) === String(data.chat_id)
                                || (data.db_chat_id && String(id) === String(data.db_chat_id));
                            const currentDialog = selectedDialogRef.current;
                            const targetId = Number(data.message_id !== undefined ? data.message_id : data.message?.id)
                            const effectiveAccountId = Number(data.account_id || (selectedAccount === -1 ? currentDialog?.tg_account_id : selectedAccount))

                            if (effectiveAccountId && targetId) {
                                removeMessagesFromLocalState(
                                    effectiveAccountId,
                                    [data.chat_id, data.db_chat_id, currentDialog?.chat_id, currentDialog?.tg_chat_id, currentDialog?.id],
                                    [targetId],
                                    Boolean(currentDialog && isMatch(currentDialog.chat_id)),
                                )
                            }

                            if (currentDialog && isMatch(currentDialog.chat_id) && (selectedTopicIdRef.current === undefined || Number(data.topic_id) === Number(selectedTopicIdRef.current))) {
                                console.log("WS DELETE MESSAGE matches current dialog, message removed from visible history.");
                            }
                            void fetchDialogs(false)
                        } else if (data.type === 'chat_update') {
                            if (selectedAccount === -1 || !data.account_id || data.account_id === selectedAccount) {
                                fetchDialogs(false)
                            }
                        } else if (data.type === 'messages_read') {
                            const currentDialog = selectedDialogRef.current;
                            if (currentDialog && (currentDialog.chat_id === data.chat_id || (data.db_chat_id && currentDialog.chat_id === data.db_chat_id))) {
                                setMessages(prev => prev.map(m => {
                                    const tgId = m.tg_message_id || m.id;
                                    return (m.outgoing && tgId <= data.max_message_id) 
                                        ? { ...m, is_read: true } 
                                        : m;
                                }))
                            }
                        } else if (data.type === 'contact_update') {
                            // Update Dialogs list instantly
                            setDialogs((prev: Dialog[]) => prev.map((d: Dialog) => 
                                d.chat_id === data.contact.chat_id 
                                    ? { 
                                        ...d, 
                                        first_name: data.contact.first_name, 
                                        last_name: data.contact.last_name, 
                                        username: data.contact.username, 
                                        photo_url: data.contact.photo_url 
                                    } 
                                    : d
                            ))
                            
                            // Update Selected Dialog instantly if it's the currently opened chat
                            const currentDialog = selectedDialogRef.current;
                            if (currentDialog && currentDialog.chat_id === data.contact.chat_id) {
                                setSelectedDialog({
                                    ...currentDialog,
                                    first_name: data.contact.first_name,
                                    last_name: data.contact.last_name,
                                    username: data.contact.username,
                                    photo_url: data.contact.photo_url
                                })
                            }
                        }
                    } catch (e) {
                        console.error('WebSocket message parse error:', e)
                    }
                }
                ws.onclose = () => {
                    if (heartbeatInterval) {
                        clearInterval(heartbeatInterval)
                        heartbeatInterval = null
                    }
                    if (!isClosed) {
                        reconnectTimeout = setTimeout(() => {
                            reconnectDelay = Math.min(reconnectDelay * 2, 30000)
                            connect()
                        }, reconnectDelay)
                    }
                }
                ws.onerror = () => { ws?.close() }
            }

            connect()

            return () => {
                isClosed = true
                if (reconnectTimeout) clearTimeout(reconnectTimeout)
                if (heartbeatInterval) clearInterval(heartbeatInterval)
                ws?.close()
            }
        }
    }, [selectedAccount])

    const getInitials = (dialog: Dialog) => {
        const first = dialog.title || dialog.first_name || dialog.username || '';
        const last = dialog.last_name || '';
        if (first) {
            return (first.charAt(0) + (last ? last.charAt(0) : '')).toUpperCase();
        }
        return '?';
    };

    // Перезагрузка диалогов при смене папки
    useEffect(() => {
        if (selectedAccount && selectedAccount !== -1) {
            const timer = setTimeout(() => {
                fetchDialogs()
            }, 300)
            return () => clearTimeout(timer)
        }
    }, [selectedFolderId, selectedAccount])

    const syncMissingDialogAvatars = (accountId: number, items: Dialog[]) => {
        if (accounts.find(account => account.id === accountId)?.status !== 'active') return
        const chatIds = items
            .filter(item => ['bot', 'channel', 'group', 'supergroup', 'forum'].includes(item.chat_type || item.type || ''))
            .filter(item => !(item.photo_url || item.contact_photo_url || item.photo))
            .map(item => item.chat_id)
            .filter((chatId): chatId is number => Boolean(chatId))
            .filter(chatId => !avatarSyncInFlightRef.current.has(`${accountId}:${chatId}`))
            .slice(0, 4)

        if (!chatIds.length) return
        chatIds.forEach(chatId => avatarSyncInFlightRef.current.add(`${accountId}:${chatId}`))
        accountsApi.syncAvatars(accountId, chatIds)
            .then(response => {
                const avatars = response.data?.avatars || []
                const avatarMap = new Map<number, string>(
                    avatars
                        .filter((item: any) => item.photo_url)
                        .map((item: any) => [Number(item.chat_id), item.photo_url])
                )
                if (!avatarMap.size) return
                setDialogs(previous => previous.map(item => {
                    const photoUrl = avatarMap.get(Number(item.chat_id))
                    return photoUrl ? { ...item, photo_url: photoUrl } : item
                }))
            })
            .catch(error => console.error('Avatar sync failed', error))
            .finally(() => chatIds.forEach(chatId => avatarSyncInFlightRef.current.delete(`${accountId}:${chatId}`)))
    }

    const fetchDialogs = async (showLoader = true) => {
        if (!selectedAccount) return
        if (showLoader) setLoading(true)
        loadingDialogsRef.current = true
        try {
            let data: Dialog[] = []
            if (selectedAccount === -1) {
                const res = await chatsApi.getAll()
                data = res.data.map((chat: any) => ({
                    ...chat,
                    chat_id: chat.tg_chat_id || chat.id,
                    db_chat_id: chat.id,
                    username: chat.username || chat.contact_username,
                    title: chat.title || chat.contact_name,
                    photo_url: chat.photo_url || chat.contact_photo_url,
                    last_message_date: chat.last_message_at,
                    last_message: chat.last_message || chat.last_message_text
                }))
                setFolders([]) // Папки недоступны в режиме «Все аккаунты»
                setHasMoreDialogs(false)
                setDialogCursor(null)
            } else {
                const response = await accountsApi.getDialogs(selectedAccount, 30, selectedFolderId)
                data = response.data.dialogs || []
                setHasMoreDialogs(Boolean(response.data.has_more))
                setDialogCursor(response.data.next_offset || null)
            }
            const safeData = data || []
            setDialogs(safeData)
            if (selectedAccount !== -1) syncMissingDialogAvatars(selectedAccount, safeData)
            return safeData
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки диалогов')
        } finally {
            setLoading(false)
            loadingDialogsRef.current = false
        }
    }

    const loadMoreDialogs = async () => {
        if (!selectedAccount || selectedAccount === -1 || !hasMoreDialogs || !dialogCursor || loadingDialogsRef.current) return
        
        loadingDialogsRef.current = true
        setLoadingMoreDialogs(true)
        try {
            const offsetDate = typeof dialogCursor.offset_date === 'number'
                ? dialogCursor.offset_date
                : dialogCursor.offset_date
                    ? Math.floor(new Date(dialogCursor.offset_date).getTime() / 1000)
                    : 0
            const response = await accountsApi.getDialogs(
                selectedAccount,
                30,
                selectedFolderId,
                offsetDate,
                dialogCursor.offset_id,
                dialogCursor.offset_peer_id,
            )
            const newData = response.data.dialogs || []
            const existingIds = new Set(dialogs.map(item => item.chat_id))
            const filteredNew = newData.filter((item: Dialog) => !existingIds.has(item.chat_id))
            
            if (filteredNew.length === 0) {
                setHasMoreDialogs(false)
                setDialogCursor(null)
            } else {
                setDialogs(previous => [...previous, ...filteredNew])
                syncMissingDialogAvatars(selectedAccount, filteredNew)
                setHasMoreDialogs(Boolean(response.data.has_more && response.data.next_offset))
                setDialogCursor(response.data.next_offset || null)
            }
        } catch (error) {
            message.error('Ошибка подгрузки диалогов')
        } finally {
            setLoadingMoreDialogs(false)
            loadingDialogsRef.current = false
        }
    }

    const fetchMessages = async (chatId: number, topicId: number | undefined = selectedTopicIdRef.current) => {
        const currentAccountSelection = selectedAccountRef.current
        if (!currentAccountSelection) return

        const effectiveAccountId = currentAccountSelection === -1 ? selectedDialogRef.current?.tg_account_id : currentAccountSelection
        if (!effectiveAccountId) return
        const requestGeneration = ++messageRequestGenerationRef.current

        // Show cached messages immediately (no loading flash)
        const cacheKey = `${effectiveAccountId}_${chatId}_${topicId ?? 'all'}`
        if (messageCache.current[cacheKey]) {
            setMessages(messageCache.current[cacheKey])
            setLoadingMessages(false)
        } else {
            setMessages([])
            setLoadingMessages(true)
        }

        try {
            let messagesData: Message[] = []
            {
                const response = await accountsApi.getMessages(effectiveAccountId, chatId, 50, 0, topicId)
                messagesData = response.data.messages.map((msg: any) => ({
                    ...msg,
                    from_user: msg.from_user || (msg.sender_name ? {
                        id: msg.sender_peer_id,
                        first_name: msg.sender_name,
                        username: msg.sender_username,
                    } : null),
                }))
                messageCache.current[cacheKey] = messagesData
                if (requestGeneration !== messageRequestGenerationRef.current
                    || !isSelectedConversation(effectiveAccountId, chatId, topicId)) return

                // Update read_only status from server response
                if (response.data.read_only !== undefined) {
                    setSelectedDialog(prev => prev && [prev.chat_id, prev.tg_chat_id, prev.id].some(id => id !== undefined && Number(id) === Number(chatId))
                        ? { ...prev, read_only: response.data.read_only }
                        : prev)
                    setDialogs(prev => prev.map(d => 
                        (d.chat_id === chatId || d.tg_chat_id === chatId) 
                            ? { ...d, read_only: response.data.read_only } 
                            : d
                    ))
                }
                
                // Mark as read in Telegram
                const d = dialogs.find(d => d.chat_id === chatId || d.tg_chat_id === chatId)
                if (!response.data.read_only && d && (d.unread_count || 0) > 0) {
                    const maxMessageId = messagesData.reduce((max, item) => Math.max(max, item.id || item.tg_message_id || 0), 0)
                    accountsApi.markAsRead(effectiveAccountId, chatId, topicId, maxMessageId).catch(e => console.error('Error marking as read:', e))
                    setDialogs(prev => prev.map(d => 
                        (d.chat_id === chatId || d.tg_chat_id === chatId) ? { ...d, unread_count: 0 } : d
                    ))
                }
            }
            setMessages(messagesData)
            setHasMoreMessages(messagesData.length >= 50)
        } catch (error: any) {
            if (requestGeneration === messageRequestGenerationRef.current
                && isSelectedConversation(effectiveAccountId, chatId, topicId)) {
                message.error(error.response?.data?.detail || 'Ошибка загрузки сообщений')
            }
        } finally {
            if (requestGeneration === messageRequestGenerationRef.current
                && isSelectedConversation(effectiveAccountId, chatId, topicId)) {
                setLoadingMessages(false)
            }
        }
    }

    const loadMoreMessages = async () => {
        if (!selectedAccount || !selectedDialog || loadingMoreRef.current || !hasMoreMessages) return
        loadingMoreRef.current = true

        const chatId = selectedDialog.chat_id || selectedDialog.tg_chat_id || selectedDialog.id
        if (!chatId) return
        
        const currentCount = messages.length
        const requestedTopicId = selectedTopicIdRef.current

        try {
            let newMessages: Message[] = []
            const effectiveAccountId = selectedAccountRef.current === -1 ? selectedDialog.tg_account_id : selectedAccountRef.current
            if (!effectiveAccountId) return
            const response = await accountsApi.getMessages(effectiveAccountId, chatId, 50, currentCount, requestedTopicId)
            newMessages = response.data.messages
            if (!isSelectedConversation(effectiveAccountId, chatId, requestedTopicId)) return

            if (newMessages.length < 50) {
                setHasMoreMessages(false)
            }

            setMessages(prev => {
                const updated = [...newMessages, ...prev]
                const cacheKey = `${effectiveAccountId}_${chatId}_${requestedTopicId ?? 'all'}`
                messageCache.current[cacheKey] = updated
                return updated
            })
        } catch (error) {
            console.error('Failed to load more messages:', error)
        } finally {
            loadingMoreRef.current = false
        }
    }

    // Track if we are viewing the current dialog vs freshly loaded
    const prevMessagesLengthRef = useRef(0)
    const isDialogSwitchRef = useRef(false)
    const userScrolledRef = useRef(false)

    useEffect(() => {
        if (messages.length === 0) {
            prevMessagesLengthRef.current = 0
            return
        }
        const container = messagesContainerRef.current
        if (!container) return

        if (isDialogSwitchRef.current) {
            // Dialog switch — column-reverse naturally pins to the start (bottom).
            isDialogSwitchRef.current = false
            userScrolledRef.current = false
        } else {
            const isAtBottom = Math.abs(container.scrollTop) < 150
            const isNewMessage = messages.length > prevMessagesLengthRef.current
            if (isNewMessage && isAtBottom) {
                // New message added while at bottom — smooth scroll
                requestAnimationFrame(() => {
                    const container = messagesContainerRef.current
                    if (container) {
                        container.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                })
            }
        }
        prevMessagesLengthRef.current = messages.length
    }, [messages])

    const scrollToBottom = () => {
        const container = messagesContainerRef.current
        if (container) {
            container.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    const scrollAndHighlightMessage = (messageId: number) => {
        const element = document.querySelector(`[data-msg-id="${messageId}"]`) as HTMLElement | null
        if (!element) return false

        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.style.transition = 'outline 0.3s, box-shadow 0.3s'
        element.style.outline = '3px solid rgba(0,136,204,0.55)'
        element.style.boxShadow = '0 0 0 4px rgba(0,136,204,0.16)'
        setTimeout(() => {
            element.style.outline = ''
            element.style.boxShadow = ''
        }, 2200)
        return true
    }

    useEffect(() => {
        const targetMessageId = pendingScrollMessageIdRef.current
        if (!targetMessageId || messages.length === 0) return

        const timer = setTimeout(() => {
            if (scrollAndHighlightMessage(targetMessageId)) {
                pendingScrollMessageIdRef.current = null
            }
        }, 150)

        return () => clearTimeout(timer)
    }, [messages])

    const handleScroll = (e: any) => {
        const container = e.target
        // In column-reverse, scrollTop near 0 means we are at the bottom.
        const isAtBottom = Math.abs(container.scrollTop) < 50
        setShowScrollButton(!isAtBottom && messages.length > 0)
        
        const isAtTop = Math.abs(container.scrollTop) + container.clientHeight >= container.scrollHeight - 200
        if (isAtTop) {
            loadMoreMessages()
        }

        // Track if user has manually scrolled up. We ignore the scroll event if it's during a dialog switch.
        if (!isDialogSwitchRef.current) {
            userScrolledRef.current = !isAtBottom
        }
    }

    const loadPinnedMessages = async (dialog: Dialog, topicId?: number) => {
        setPinnedMessages([])
        setShowPinnedBanner(true)
        const accountId = selectedAccountRef.current === -1 ? dialog.tg_account_id : selectedAccountRef.current
        if (!accountId || accountId === -1) return
        const chatId = dialog.chat_id || dialog.tg_chat_id || 0
        try {
            const response = await accountsApi.getPinnedMessages(accountId, chatId, topicId)
            if (!isSelectedConversation(accountId, chatId, topicId)) return
            setPinnedMessages(response.data.pinned_messages || [])
            setCurrentPinnedIndex(0)
        } catch {
            if (isSelectedConversation(accountId, chatId, topicId)) setPinnedMessages([])
        }
    }

    const handleSelectTopic = async (topic: ForumTopic) => {
        if (!selectedDialog) return
        dialogSelectionGenerationRef.current += 1
        messageRequestGenerationRef.current += 1
        setSelectedTopicId(topic.id)
        selectedTopicIdRef.current = topic.id
        setTopics(current => current.map(item => item.id === topic.id ? { ...item, unread_count: 0 } : item))
        setDialogs(current => current.map(item => item.chat_id === selectedDialog.chat_id
            ? { ...item, unread_count: Math.max(0, (item.unread_count || 0) - (topic.unread_count || 0)) }
            : item
        ))
        pendingScrollMessageIdRef.current = null
        prevMessagesLengthRef.current = 0
        await fetchMessages(selectedDialog.chat_id, topic.id)
        await loadPinnedMessages(selectedDialog, topic.id)
    }

    const handleSelectDialog = async (dialog: Dialog, targetMessageId?: number, preferredTopicId?: number) => {
        const selectionGeneration = ++dialogSelectionGenerationRef.current
        messageRequestGenerationRef.current += 1
        setSelectedDialog(dialog)
        selectedDialogRef.current = dialog
        setMessages([])
        setPinnedMessages([])
        setLoadingMessages(true)
        pendingScrollMessageIdRef.current = targetMessageId || null
        // Signal that this is a dialog switch so scroll goes to bottom instantly
        isDialogSwitchRef.current = !targetMessageId
        userScrolledRef.current = Boolean(targetMessageId)
        
        prevMessagesLengthRef.current = 0

        let topicId: number | undefined
        if (dialog.is_forum || dialog.chat_type === 'forum' || dialog.type === 'forum') {
            const accountId = selectedAccountRef.current === -1 ? dialog.tg_account_id : selectedAccountRef.current
            setLoadingTopics(true)
            try {
                if (accountId && accountId !== -1) {
                    const response = await accountsApi.getTopics(accountId, dialog.chat_id)
                    if (selectionGeneration !== dialogSelectionGenerationRef.current) return
                    const availableTopics = (response.data.topics || []).filter((item: ForumTopic) => !item.is_hidden)
                    setTopics(availableTopics)
                    const preferred = availableTopics.find((item: ForumTopic) => item.id === preferredTopicId)
                    const initialTopic = preferred || availableTopics.find((item: ForumTopic) => item.is_pinned) || availableTopics.find((item: ForumTopic) => item.is_general) || availableTopics[0]
                    topicId = initialTopic?.id
                    if (initialTopic) {
                        setTopics(availableTopics.map((item: ForumTopic) => item.id === initialTopic.id ? { ...item, unread_count: 0 } : item))
                        setDialogs(current => current.map(item => item.chat_id === dialog.chat_id
                            ? { ...item, unread_count: Math.max(0, (item.unread_count || 0) - (initialTopic.unread_count || 0)) }
                            : item
                        ))
                    }
                }
            } catch (error) {
                if (selectionGeneration !== dialogSelectionGenerationRef.current) return
                console.error('Error loading topics:', error)
                setTopics([])
            } finally {
                if (selectionGeneration === dialogSelectionGenerationRef.current) setLoadingTopics(false)
            }
        } else {
            setTopics([])
        }

        if (selectionGeneration !== dialogSelectionGenerationRef.current) return
        setSelectedTopicId(topicId)
        selectedTopicIdRef.current = topicId
        await fetchMessages(dialog.chat_id, topicId)

        if (selectionGeneration !== dialogSelectionGenerationRef.current) return
        if (!topicId) {
            setDialogs(prev => prev.map(item =>
                item.chat_id === dialog.chat_id ? { ...item, unread_count: 0 } : item
            ))
            if (dialog.unread_count > 0) {
                try {
                    const accountId = selectedAccountRef.current === -1 ? dialog.tg_account_id : selectedAccountRef.current
                    if (accountId && accountId !== -1) {
                        await accountsApi.markAsRead(accountId, dialog.chat_id || dialog.tg_chat_id || 0)
                    }
                } catch (error) {
                    console.error('Error marking as read:', error)
                }
            }
        }
        await loadPinnedMessages(dialog, topicId)
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setMediaFile(e.target.files[0])
            setMediaCaption("")
        }
        // clear input
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        if (!selectedDialog) return
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        if (!selectedDialog) return
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setMediaFile(e.dataTransfer.files[0])
            // Do not reset caption so user can keep typing
        }
    }

    const handleSendMedia = async () => {
        if (!mediaFile || !selectedDialog) return
        const activeTopic = topics.find(topic => topic.id === selectedTopicIdRef.current)
        if (selectedDialog.can_send_messages === false || activeTopic?.is_closed) {
            message.error(activeTopic?.is_closed ? 'Топик закрыт' : 'Отправка сообщений недоступна')
            return
        }
        
        const targetAccountId = selectedAccount === -1 ? selectedDialog.tg_account_id : selectedAccount
        if (!targetAccountId) {
            message.error('Ошибка: Не удалось определить аккаунт для отправки')
            return
        }

        const chatId = selectedDialog.chat_id || selectedDialog.tg_chat_id || selectedDialog.id
        if (!chatId) {
            message.error('Ошибка: Не удалось определить ID чата')
            return
        }

        setUploadingMedia(true)
        try {
            await accountsApi.sendMedia(targetAccountId, chatId, mediaFile, mediaCaption, selectedTopicIdRef.current)
            message.success('Файл отправлен')
            
            // Move dialog to top of list + update last message preview
            const textPreview = mediaCaption ? `📎 ${mediaCaption}` : '📎 Медиа'
            setDialogs(prev => {
                const updated = prev.map(d =>
                    (d.chat_id || d.tg_chat_id || d.id) === chatId
                        ? { ...d, last_message: textPreview, last_message_date: new Date().toISOString() }
                        : d
                )
                const idx = updated.findIndex(d => (d.chat_id || d.tg_chat_id || d.id) === chatId)
                if (idx > 0) {
                    const [moved] = updated.splice(idx, 1)
                    updated.unshift(moved)
                }
                return updated
            })
            
            setMediaFile(null)
            setMediaCaption("")
            // Refresh messages after send
            setTimeout(() => fetchMessages(chatId, selectedTopicIdRef.current), 500)
        } catch (error: any) {
            console.error('Error sending media:', error)
            message.error(`Ошибка: ${error.response?.data?.detail || 'Не удалось отправить файл'}`)
        } finally {
            setUploadingMedia(false)
        }
    }

    const handleSendMessage = async () => {
        if (!selectedAccount || !selectedDialog || (!newMessage.trim() && !mediaFile)) return
        const activeTopic = topics.find(topic => topic.id === selectedTopicIdRef.current)
        if (selectedDialog.can_send_messages === false || activeTopic?.is_closed) {
            message.error(activeTopic?.is_closed ? 'Топик закрыт' : 'Отправка сообщений недоступна')
            return
        }
        const targetAccountId = selectedAccount === -1 ? selectedDialog.tg_account_id : selectedAccount
        if (!targetAccountId) {
            message.error('Ошибка: Не удалось определить аккаунт для отправки')
            return
        }

        const chatId = selectedDialog.chat_id || selectedDialog.tg_chat_id || selectedDialog.id
        if (!chatId) {
            message.error('Ошибка: Не удалось определить ID чата')
            return
        }

        const textToSend = newMessage
        const fileToSend = mediaFile
        
        if (editMessageId) {
            setSending(true)
            try {
                await accountsApi.editMessage(targetAccountId, chatId, editMessageId, textToSend)
                setMessages(prev => prev.map(m => (m.tg_message_id || m.id) === editMessageId ? { ...m, text: textToSend, is_edited: true } : m))
                setEditMessageId(null)
                setNewMessage('')
            } catch (error: any) {
                message.error(error.response?.data?.detail || 'Ошибка редактирования')
            } finally {
                setSending(false)
            }
            return
        }

        setNewMessage('')
        setMediaFile(null)
        setShowEmojiPicker(false)
        // ────────────────────────────────────────────────

        setSending(true)
        try {
            if (fileToSend) {
                await accountsApi.sendMedia(targetAccountId, chatId, fileToSend, textToSend, selectedTopicIdRef.current, replyTo?.id)
            } else {
                await accountsApi.sendMessage(targetAccountId, chatId, textToSend, replyTo?.id, selectedTopicIdRef.current)
            }
            setReplyTo(null)
            // Silently refresh in background to get real message ids
            setTimeout(() => fetchMessages(chatId, selectedTopicIdRef.current), 1500)
        } catch (error: any) {
            // Rollback is not needed because we didn't optimistically update
            setNewMessage(textToSend)
            setMediaFile(fileToSend)
            message.error(error.response?.data?.detail || 'Ошибка отправки')
        } finally {
            setSending(false)
        }
    }

    const handleMouseDown = () => setIsResizing(true)

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing || !containerRef.current) return
            const containerRect = containerRef.current.getBoundingClientRect()
            const newWidth = e.clientX - containerRect.left
            if (newWidth >= 200 && newWidth <= 600) setSiderWidth(newWidth)
        }
        const handleMouseUp = () => setIsResizing(false)
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing])

    // Закрепить/открепить сообщение
    const handlePinMessage = async (messageId: number) => {
        if (!selectedAccount || !selectedDialog) return
        try {
            const accId = selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount
            await accountsApi.pinMessage(accId, selectedDialog.chat_id || selectedDialog.tg_chat_id || 0, messageId)
            const res = await accountsApi.getPinnedMessages(accId, selectedDialog.chat_id || selectedDialog.tg_chat_id || 0, selectedTopicIdRef.current)
            setPinnedMessages(res.data.pinned_messages || [])
            setCurrentPinnedIndex(0)
            setShowPinnedBanner(true)
            message.success('Сообщение закреплено')
        } catch (e: any) {
            message.error(e.response?.data?.detail || 'Ошибка закрепления')
        }
        setContextMenu(null)
    }

    const handleUnpinMessage = async (messageId: number) => {
        if (!selectedAccount || !selectedDialog) return
        try {
            const accId = selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount
            await accountsApi.unpinMessage(accId, selectedDialog.chat_id || selectedDialog.tg_chat_id || 0, messageId)
            message.success('Сообщение откреплено')
            setPinnedMessages(prev => prev.filter(p => p.tg_message_id !== messageId))
        } catch (e: any) {
            message.error(e.response?.data?.detail || 'Ошибка открепления')
        }
        setContextMenu(null)
    }

    // Контекстное меню сообщения (правый клик)
    const handleMessageContextMenu = (e: React.MouseEvent, msg: Message) => {
        try {
            if (!selectedDialog || selectedDialog.read_only) return
            e.preventDefault()
            const safePinned = Array.isArray(pinnedMessages) ? pinnedMessages : []
            const isPinned = safePinned.some(p => p.tg_message_id === (msg.tg_message_id || msg.id))
            setContextMenu({ x: e.clientX, y: e.clientY, messageId: msg.tg_message_id || msg.id, isPinned, msgText: msg.text, msgOutgoing: msg.outgoing })
        } catch (err: any) {
            console.error('Context menu error:', err)
            message.error('Ошибка меню: ' + err.message)
        }
    }

    const handleSearchUser = async () => {
        if (!selectedAccount || !newChatTarget.trim()) return
        setSearchingUser(true)
        setSearchResults([])
        setSelectedSearchResult(null)
        try {
            const res = await accountsApi.searchUser(selectedAccount, newChatTarget.trim())
            if (res.data?.found && res.data.users?.length > 0) {
                setSearchResults(res.data.users)
            } else {
                message.warning('Пользователь не найден')
            }
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка поиска')
        } finally {
            setSearchingUser(false)
        }
    }

    const handleSendNewChat = async () => {
        if (!selectedAccount || !newChatTarget.trim() || !newChatMessage.trim()) return
        setSendingNewChat(true)
        try {
            const res = await accountsApi.sendToNewContact(
                selectedAccount,
                newChatType === 'username' ? newChatTarget : undefined,
                newChatType === 'phone' ? newChatTarget : undefined,
                newChatMessage
            )
            message.success('Сообщение отправлено!')
            setNewChatModalOpen(false)
            setNewChatTarget('')
            setNewChatMessage('')
            
            // Fetch messages directly from backend immediately after sending to bypass cache
            setTimeout(async () => {
                const newDialogs = await fetchDialogs()
                if (res.data && res.data.chat_id) {
                    // Force cache invalidation for this chat
                    const cacheKey = `${selectedAccount}_${res.data.chat_id}_all`
                    delete messageCache.current[cacheKey]

                    const createdDialog = newDialogs?.find((d: any) => String(d.chat_id) === String(res.data.chat_id) || String(d.db_chat_id) === String(res.data.chat_id))
                    if (createdDialog) {
                        handleSelectDialog(createdDialog)
                    } else {
                        const fallbackDialog = {
                            id: res.data.chat_id,
                            chat_id: res.data.chat_id,
                            db_chat_id: res.data.chat_id,
                            type: 'private',
                            name: newChatType === 'username' ? newChatTarget : newChatTarget,
                            username: newChatType === 'username' ? newChatTarget : undefined,
                            unread_count: 0,
                            last_message: newChatMessage,
                            last_message_date: new Date().toISOString(),
                            tg_account_id: selectedAccount
                        }
                        setDialogs(prev => [fallbackDialog as Dialog, ...prev])
                        handleSelectDialog(fallbackDialog as Dialog)
                    }
                }
            }, 1500)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка отправки')
        } finally {
            setSendingNewChat(false)
        }
    }

    const handleAddToContacts = async () => {
        if (!selectedDialog) return
        try {
            await contactsApi.create({
                username: selectedDialog.username || undefined,
                first_name: selectedDialog.first_name || undefined,
                last_name: selectedDialog.last_name || undefined,
                notes: `Добавлен из чата`
            })
            message.success('Контакт добавлен')
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка добавления')
        }
    }

    const handleDeleteChat = async (deleteInTelegram: boolean) => {
        if (!selectedAccount || !selectedDialog) return
        try {
            await accountsApi.deleteChat(selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount, selectedDialog.chat_id, deleteInTelegram)
            message.success(deleteInTelegram ? 'Чат удалён везде' : 'Чат удалён из TG Manager')
            setDeleteChatModalOpen(false)
            setSelectedDialog(null)
            fetchDialogs()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const handleBlockUser = async () => {
        if (!selectedAccount || !selectedDialog) return
        try {
            const accId = selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount
            await accountsApi.blockUser(accId, selectedDialog.chat_id)
            message.success('Пользователь заблокирован')
            setSelectedDialog({ ...selectedDialog, is_blocked: true })
            fetchDialogs()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка блокировки')
        }
    }

    const handleUnblockUser = async () => {
        if (!selectedAccount || !selectedDialog) return
        try {
            const accId = selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount
            await accountsApi.unblockUser(accId, selectedDialog.chat_id)
            message.success('Пользователь разблокирован')
            setSelectedDialog({ ...selectedDialog, is_blocked: false })
            fetchDialogs()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка разблокировки')
        }
    }

    const [continueChatModalOpen, setContinueChatModalOpen] = useState(false)
    const [continueTargetAccount, setContinueTargetAccount] = useState<number | null>(null)
    const [continuingChat, setContinuingChat] = useState(false)

    const handleContinueChat = async () => {
        if (!selectedDialog || !continueTargetAccount) return
        setContinuingChat(true)
        try {
            await accountsApi.sendToNewContact(
                continueTargetAccount,
                selectedDialog.username || undefined,
                undefined,
                `Продолжение диалога`
            )
            handleSelectAccount(continueTargetAccount)
            setContinueChatModalOpen(false)
            message.success(`Переключено на аккаунт ${accounts.find(a => a.id === continueTargetAccount)?.first_name || continueTargetAccount}`)
            setTimeout(() => fetchDialogs(), 1000)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка продолжения диалога')
        } finally {
            setContinuingChat(false)
        }
    }

    const handleRefresh = () => {
        if (activeTab === 'telegram') {
            fetchDialogs(true)
            if (selectedDialog) {
                const chatId = selectedDialog.chat_id || selectedDialog.tg_chat_id || selectedDialog.id
                if (chatId) {
                    fetchMessages(chatId)
                }
            }
        } else {
            fetchInternalChats()
            if (selectedInternalChat) {
                fetchInternalMessages(selectedInternalChat.id)
            }
        }
    }

    const activeForumTopic = topics.find(topic => topic.id === selectedTopicId)
    const selectedAccountRecord = accounts.find(account => account.id === selectedAccount)
    const selectedAccountIsHistorical = Boolean(selectedAccountRecord && selectedAccountRecord.status !== 'active')
    const conversationReadOnly = Boolean(selectedAccountIsHistorical || selectedDialog?.read_only || selectedDialog?.can_send_messages === false || activeForumTopic?.is_closed)

    return (
        <div ref={containerRef} style={{ padding: 24, height: 'calc(100vh - 64px)', userSelect: isResizing ? 'none' : 'auto' }}>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
                {activeTab === 'telegram' && (
                    <>
                        <Select<AccountStatusFilter>
                            style={{ width: 180 }}
                            value={accountStatusFilter}
                            onChange={handleAccountStatusFilter}
                            options={[
                                { value: 'all', label: 'Все доступные' },
                                { value: 'active', label: 'Активные' },
                                { value: 'banned', label: 'Заблокированные' },
                                { value: 'unavailable', label: 'Недоступные' },
                            ]}
                        />
                        <Select
                            style={{ width: 300 }}
                            placeholder="Выберите аккаунт"
                            value={selectedAccount}
                            onChange={handleSelectAccount}
                        >
                            {visibleAccounts.map((acc: Account) => (
                                <Select.Option key={acc.id} value={acc.id}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Badge status={acc.status === 'active' ? 'success' : acc.status === 'banned' ? 'error' : 'warning'} />
                                        <span style={{ marginLeft: 8 }}>
                                            {acc.first_name || acc.phone}
                                            {acc.username ? ` (@${acc.username})` : ''}
                                            {acc.status === 'banned' ? ' — заблокирован Telegram' : ''}
                                            {['inactive', 'limited'].includes(acc.status) ? ' — сессия недоступна' : ''}
                                        </span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </>
                )}
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading || loadingInternalChats}>
                    Обновить
                </Button>
                {/* {activeTab === 'telegram' && (
                    <Tooltip title="Выгрузить в Excel">
                        <Button
                            icon={<FileExcelOutlined />}
                            onClick={exportChats}
                            loading={loadingExport}
                            style={{ borderColor: '#10b981', color: '#10b981' }}
                        >
                            Excel
                        </Button>
                    </Tooltip>
                )} */}
                <Button
                    icon={<PlusOutlined />}
                    onClick={() => setNewChatModalOpen(true)}
                    disabled={activeTab === 'telegram' && (!selectedAccount || selectedAccountIsHistorical)}
                >
                    Новый чат
                </Button>
            </div>

            <div style={{ display: 'flex', height: 'calc(100% - 50px)' }}>
                <div style={{
                    width: siderWidth,
                    minWidth: 200,
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{ padding: '12px 12px 4px' }}>
                        <Segmented
                            block
                            value={activeTab}
                            onChange={(val) => {
                                const v = val as 'telegram' | 'internal'
                                setActiveTab(v)
                                localStorage.setItem('chatsActiveTab', v)
                            }}
                            options={[
                                { label: 'Telegram', value: 'telegram', icon: <SendOutlined /> },
                                { label: 'Внутренние', value: 'internal', icon: <TeamOutlined /> },
                            ]}
                        />
                    </div>

                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                        <Input
                            placeholder="Поиск..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-muted)', fontSize: 14 }} />}
                            value={rawSearch}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            allowClear
                            onClear={() => { setRawSearch(''); setSearchQuery('') }}
                            size="small"
                            style={{
                                background: 'var(--bg-subtle)',
                                borderColor: 'var(--border)',
                                borderRadius: 8,
                            }}
                        />
                    </div>

                    {/* Вкладки папок Telegram */}
                    {folders.length > 0 && selectedAccount !== -1 && activeTab === 'telegram' && (
                        <div style={{ 
                            display: 'flex', 
                            gap: 4, 
                            padding: '6px 12px', 
                            borderBottom: '1px solid var(--border)',
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            scrollbarWidth: 'thin'
                        }}>
                            <Button 
                                size="small" 
                                type={selectedFolderId === undefined ? 'primary' : 'text'}
                                style={{ borderRadius: 12, fontSize: 12 }}
                                onClick={() => setSelectedFolderId(undefined)}
                            >
                                Все
                            </Button>
                            {folders.map(f => (
                                <Button
                                    key={f.id}
                                    size="small"
                                    type={selectedFolderId === f.id ? 'primary' : 'text'}
                                    style={{ borderRadius: 12, fontSize: 12 }}
                                    onClick={() => setSelectedFolderId(f.id)}
                                >
                                    {f.emoticon ? `${f.emoticon} ` : ''}{safeText(f.title)}
                                </Button>
                            ))}
                        </div>
                    )}

                    <div 
                        style={{ flex: 1, overflow: 'auto' }}
                        onScroll={(e) => {
                            const { scrollTop, clientHeight, scrollHeight } = e.currentTarget
                            if (scrollHeight - scrollTop - clientHeight < 50) {
                                if (activeTab === 'telegram' && !loadingDialogsRef.current && hasMoreDialogs) {
                                    loadMoreDialogs()
                                }
                            }
                        }}
                    >
                        {activeTab === 'telegram' ? (
                            loading ? (
                                <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                            ) : (
                                <>
                                <List
                                    rowKey="chat_id"
                                    dataSource={dialogs.filter((d: Dialog) => {
                                        if (!searchQuery) return true
                                        const query = searchQuery.toLowerCase()
                                        return (d.username || '').toLowerCase().includes(query) ||
                                            (d.first_name || '').toLowerCase().includes(query) ||
                                            (d.last_name || '').toLowerCase().includes(query) ||
                                            (d.title || '').toLowerCase().includes(query)
                                    })}
                                    renderItem={(dialog: Dialog) => (
                                        <List.Item
                                            onClick={() => handleSelectDialog(dialog)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '12px 16px',
                                                background: selectedDialog?.chat_id === dialog.chat_id ? 'var(--bg-subtle)' : 'transparent',
                                                borderBottom: '1px solid var(--border)',
                                                opacity: dialog.is_deleted || dialog.is_account_blocked ? 0.7 : 1,
                                            }}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Badge count={dialog.unread_count} size="small">
                                                        <Avatar
                                                            src={dialog.photo_url || dialog.contact_photo_url || dialog.photo || undefined}
                                                            style={{
                                                                background: dialog.is_deleted ? '#ef4444' : (dialog.is_account_blocked ? '#6b7280' : '#0088cc'),
                                                                color: '#fff',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {!(dialog.photo_url || dialog.contact_photo_url || dialog.photo) && getInitials(dialog)}
                                                        </Avatar>
                                                    </Badge>
                                                }
                                                title={
                                                    <span style={{ color: 'var(--text-primary)' }}>
                                                        {dialog.chat_type === 'bot' ? '🤖 ' : dialog.chat_type === 'channel' ? '📢 ' : ['group', 'supergroup', 'forum'].includes(dialog.chat_type || dialog.type || '') ? '👥 ' : ''}
                                                        {safeText(dialog.title) || safeText(dialog.first_name) || safeText(dialog.username) || 'Unknown'}
                                                        {!dialog.title && dialog.last_name ? ` ${safeText(dialog.last_name)}` : ''}
                                                    </span>
                                                }
                                                description={
                                                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                        {selectedAccount === -1 && dialog.account_phone && (
                                                            <Tag color={dialog.read_only ? 'red' : 'blue'} style={{ marginRight: 4, fontSize: 10 }}>
                                                                {dialog.account_username ? `@${dialog.account_username}` : dialog.account_phone}
                                                            </Tag>
                                                        )}
                                                        {safeText(dialog.last_message)?.substring(0, 40) || 'Нет сообщений'}
                                                    </span>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                    locale={{ emptyText: 'Нет диалогов' }}
                                />
                                {loadingMoreDialogs && (
                                    <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                        <Spin size="small" />
                                    </div>
                                )}
                            </>
                            )
                        ) : (
                            loadingInternalChats ? (
                                <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                            ) : (
                                <List
                                    dataSource={internalChats.filter((c: InternalChat) => {
                                        if (!searchQuery) return true
                                        const query = searchQuery.toLowerCase()
                                        return (c.other_user.full_name || c.other_user.username || '').toLowerCase().includes(query)
                                    })}
                                    renderItem={(chat: InternalChat) => (
                                        <List.Item
                                            onClick={() => handleSelectInternalChat(chat)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '12px 16px',
                                                background: selectedInternalChat?.id === chat.id ? 'var(--bg-subtle)' : 'transparent',
                                                borderBottom: '1px solid var(--border)',
                                            }}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Badge count={chat.unread_count} size="small">
                                                        <Avatar src={chat.other_user.avatar_url}>
                                                            {(chat.other_user.full_name || chat.other_user.username || 'U')[0].toUpperCase()}
                                                        </Avatar>
                                                    </Badge>
                                                }
                                                title={
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span style={{ color: 'var(--text-primary)', fontWeight: chat.unread_count > 0 ? 600 : 400 }}>
                                                            {chat.other_user.full_name || chat.other_user.username}
                                                        </span>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                    locale={{ emptyText: 'Нет чатов' }}
                                />
                            )
                        )}
                    </div>
                </div>

                <div onMouseDown={handleMouseDown} style={{ width: 6, cursor: 'col-resize', background: 'transparent', flexShrink: 0 }} />

                <Content 
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        marginLeft: 10,
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        flex: 1,
                        position: 'relative'
                    }}
                >
                    {isDragging && (
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0, 136, 204, 0.1)', border: '2px dashed #0088cc',
                            zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 8, pointerEvents: 'none'
                        }}>
                            <div style={{ background: 'var(--bg-surface)', padding: '20px 40px', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                                <InboxOutlined style={{ fontSize: 48, color: '#0088cc', marginBottom: 16 }} />
                                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Отпустите файл для отправки</h3>
                            </div>
                        </div>
                    )}
                    {activeTab === 'telegram' ? (
                        selectedDialog ? (
                            <Fragment>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {selectedDialog.title || `${selectedDialog.first_name || ''} ${selectedDialog.last_name || ''}`.trim() || selectedDialog.username || 'Unknown'}
                                        {!conversationReadOnly && (selectedDialog.chat_type || selectedDialog.type) === 'private' && (
                                            <Button type="text" icon={<UserAddOutlined />} onClick={handleAddToContacts} style={{ color: '#94a3b8' }} />
                                        )}
                                        {selectedDialog.username && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>@{selectedDialog.username}</span>}
                                        {(selectedDialog.is_deleted || selectedDialog.read_only || selectedDialog.can_send_messages === false) && (
                                            <span style={{ color: '#ef4444', fontSize: 12, padding: '2px 8px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: 4 }}>
                                                🔴 Только чтение
                                            </span>
                                        )}
                                        {selectedDialog.is_blocked && (
                                            <span style={{ color: '#ef4444', fontSize: 12, padding: '2px 8px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: 4 }}>
                                                🚫 Заблокирован
                                            </span>
                                        )}
                                    </h3>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        {!conversationReadOnly && ['private', 'bot'].includes(selectedDialog.chat_type || selectedDialog.type || '') && (
                                            selectedDialog.is_blocked ? (
                                                <Popconfirm title="Разблокировать пользователя?" onConfirm={handleUnblockUser}>
                                                    <Button type="text" icon={<span style={{ fontSize: 16 }}>✅</span>} style={{ color: '#10b981' }} title="Разблокировать" />
                                                </Popconfirm>
                                            ) : (
                                                <Popconfirm title="Заблокировать пользователя?" onConfirm={handleBlockUser}>
                                                    <Button type="text" icon={<span style={{ fontSize: 16 }}>🚫</span>} style={{ color: '#ef4444' }} title="Заблокировать" />
                                                </Popconfirm>
                                            )
                                        )}
                                        <Button type="text" icon={<DeleteOutlined />} onClick={() => setDeleteChatModalOpen(true)} disabled={conversationReadOnly} style={{ color: '#94a3b8' }} title="Удалить чат" />
                                    </div>
                                </div>

                                {(selectedDialog.is_forum || selectedDialog.chat_type === 'forum' || selectedDialog.type === 'forum') && (
                                    <div style={{
                                        minHeight: 44,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 12px',
                                        overflowX: 'auto',
                                        whiteSpace: 'nowrap',
                                        borderBottom: '1px solid var(--border)',
                                        background: 'var(--bg-surface)',
                                        scrollbarWidth: 'thin',
                                    }}>
                                        {loadingTopics ? <Spin size="small" /> : topics.map(topic => {
                                            const active = selectedTopicId === topic.id
                                            const color = topic.icon_color !== undefined ? `#${topic.icon_color.toString(16).padStart(6, '0').slice(-6)}` : 'var(--brand-primary)'
                                            return (
                                                <Button
                                                    key={topic.id}
                                                    size="small"
                                                    type={active ? 'primary' : 'default'}
                                                    onClick={() => handleSelectTopic(topic)}
                                                    style={{ borderRadius: 16, display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
                                                >
                                                    <span style={{ color: active ? 'inherit' : color }}>{topic.is_general ? '💬' : topic.is_pinned ? '📌' : '●'}</span>
                                                    {topic.title}
                                                    {topic.is_closed && <span>🔒</span>}
                                                    {topic.unread_count > 0 && <Badge count={topic.unread_count} size="small" />}
                                                </Button>
                                            )
                                        })}
                                        {!loadingTopics && topics.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Топики не найдены</span>}
                                    </div>
                                )}

                                {selectedDialog.read_only && (
                                    <div style={{ background: '#fff7ed', padding: '8px 16px', color: '#c2410c', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>⚠️ Режим чтения. Аккаунт недоступен.</span>
                                        <Button size="small" type="primary" ghost onClick={() => setContinueChatModalOpen(true)}>Продолжить с другого</Button>
                                    </div>
                                )}

                                {/* Баннер закреплённого сообщения */}
                                {pinnedMessages.length > 0 && showPinnedBanner && (
                                    <div 
                                        onClick={() => {
                                            const pinnedMsgId = pinnedMessages[currentPinnedIndex]?.tg_message_id || pinnedMessages[currentPinnedIndex]?.id
                                            if (!pinnedMsgId) return
                                            
                                            const tryScroll = () => {
                                                const el = document.querySelector(`[data-msg-id="${pinnedMsgId}"]`) as HTMLElement
                                                if (el) {
                                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                                    el.style.transition = 'background 0.3s'
                                                    el.style.background = 'rgba(0, 136, 204, 0.25)'
                                                    setTimeout(() => { el.style.background = '' }, 2000)
                                                    return true
                                                }
                                                return false
                                            }

                                            const cycleIndex = () => {
                                                if (pinnedMessages.length > 1) {
                                                    setCurrentPinnedIndex((prev) => (prev + 1) % pinnedMessages.length)
                                                }
                                            }

                                            if (tryScroll()) {
                                                cycleIndex()
                                            } else {
                                                // Если сообщение не найдено в текущем DOM, пробуем подгрузить историю
                                                const loadHistoryToMessage = async () => {
                                                    const hideLoading = message.loading('Загрузка истории...', 0)
                                                    try {
                                                        let currentSkip = messages.length
                                                        let found = false
                                                        const chatId = selectedDialog.chat_id || selectedDialog.tg_chat_id || selectedDialog.id
                                                        if (!chatId) return

                                                        for (let i = 0; i < 30; i++) { // Максимум 9000 сообщений вглубь
                                                            let fetchedMessages: any[] = []
                                                            const accId = selectedAccount === -1 ? selectedDialog.tg_account_id! : selectedAccount
                                                            if (!accId) break
                                                            const res = await accountsApi.getMessages(accId, chatId, 300, currentSkip, selectedTopicIdRef.current)
                                                            fetchedMessages = res.data.messages

                                                            if (!fetchedMessages || fetchedMessages.length === 0) break

                                                            const hasTarget = fetchedMessages.some((m: any) => (m.tg_message_id || m.id) === pinnedMsgId)
                                                            
                                                            setMessages(prev => {
                                                                const newMsgs = fetchedMessages.filter(fm => !prev.some(pm => (pm.tg_message_id || pm.id) === (fm.tg_message_id || fm.id)))
                                                                return [...newMsgs.reverse(), ...prev]
                                                            })
                                                            
                                                            currentSkip += fetchedMessages.length
                                                            
                                                            if (hasTarget) {
                                                                found = true
                                                                setTimeout(() => { tryScroll(); cycleIndex() }, 200)
                                                                break
                                                            }
                                                        }
                                                        if (!found) {
                                                            message.info('Сообщение находится слишком далеко в истории (не загружено)')
                                                            cycleIndex()
                                                        }
                                                    } catch (e) {
                                                        console.error(e)
                                                        message.error('Ошибка загрузки истории')
                                                    } finally {
                                                        hideLoading()
                                                    }
                                                }
                                                loadHistoryToMessage()
                                            }
                                        }}
                                        style={{ 
                                            padding: '8px 16px', 
                                            background: 'var(--bg-subtle)', 
                                            borderBottom: '1px solid var(--border)',
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 8,
                                            cursor: 'pointer',
                                            fontSize: 13
                                        }}>
                                        <span style={{ fontSize: 14 }}>📌</span>
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                            <span style={{ color: 'var(--brand-primary)', fontWeight: 500, fontSize: 12 }}>
                                                Закреплённое сообщение {pinnedMessages.length > 1 ? `(${currentPinnedIndex + 1}/${pinnedMessages.length})` : ''}
                                            </span>
                                            <span style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {pinnedMessages[currentPinnedIndex]?.text || 'Вложение'}
                                            </span>
                                        </div>
                                        <Button 
                                            type="text" 
                                            size="small" 
                                            style={{ fontSize: 12, color: 'var(--text-muted)' }}
                                            onClick={(e) => { e.stopPropagation(); setShowPinnedBanner(false) }}
                                        >✕</Button>
                                    </div>
                                )}
                                <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                                    <div ref={messagesContainerRef} onScroll={handleScroll} style={{ height: '100%', overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column-reverse', gap: 8 }}>
                                    {loadingMessages && (
                                        <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: 'var(--bg-surface)', padding: '8px 12px', borderRadius: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                            <Spin size="small" /> <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-primary)' }}>Обновление...</span>
                                        </div>
                                    )}
                                        <Fragment>
                                            {[...messages].reverse().map((msg: any) => {
                                                const msgId = msg.tg_message_id || msg.id
                                                const isSelected = isSelectMode && selectedMsgIds.includes(msgId)
                                                const messageTopicId = msg.topic_id ?? selectedTopicId
                                                const visibleReplyTo = messageTopicId && Number(messageTopicId) !== 1 && Number(msg.reply_to) === Number(messageTopicId)
                                                    ? null
                                                    : msg.reply_to
                                                return (
                                                <div key={msg.id} data-msg-id={msgId} onContextMenu={(e) => handleMessageContextMenu(e, msg)} onClick={() => { if (isSelectMode) { setSelectedMsgIds(prev => prev.includes(msgId) ? prev.filter(id => id !== msgId) : [...prev, msgId]) } }} style={{ alignSelf: msg.outgoing ? 'flex-end' : 'flex-start', maxWidth: '70%', padding: '10px 14px', borderRadius: 12, background: msg.outgoing ? 'var(--brand-primary)' : 'var(--bg-subtle)', color: msg.outgoing ? '#fff' : 'var(--text-primary)', opacity: msg.is_deleted_in_telegram ? 0.6 : 1, cursor: isSelectMode ? 'pointer' : 'default', outline: isSelected ? '2px solid var(--brand-primary)' : 'none', outlineOffset: 2, position: 'relative' }}>
                                                    {/* Reply Quote — если сообщение является ответом */}
                                                    {visibleReplyTo && (() => {
                                                        const replyMsg = messages.find((m: any) => (m.tg_message_id || m.id) == visibleReplyTo)
                                                        return (
                                                            <div
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    const el = document.querySelector(`[data-msg-id="${visibleReplyTo}"]`)
                                                                    if (el) {
                                                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                                                        ;(el as HTMLElement).style.transition = 'background 0.3s'
                                                                        ;(el as HTMLElement).style.background = 'rgba(0,184,148,0.2)'
                                                                        setTimeout(() => { (el as HTMLElement).style.background = '' }, 1500)
                                                                    }
                                                                }}
                                                                style={{
                                                                    borderLeft: '3px solid #00b894',
                                                                    padding: '4px 8px', marginBottom: 6, borderRadius: 4,
                                                                    background: msg.outgoing ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
                                                                    cursor: 'pointer', fontSize: 12, overflow: 'hidden',
                                                                }}
                                                            >
                                                                <div style={{ fontWeight: 600, color: msg.outgoing ? 'rgba(255,255,255,0.9)' : '#00b894', fontSize: 11 }}>
                                                                    {replyMsg?.from_user?.first_name || (replyMsg?.outgoing ? 'Вы' : selectedDialog?.first_name || 'Собеседник')}
                                                                </div>
                                                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.8 }}>
                                                                    {replyMsg?.text || '📎 Медиа'}
                                                                </div>
                                                            </div>
                                                        )
                                                    })()}
                                                    {msg.outgoing && (msg.from_user || msg.operator_id || !msg.operator_id) && (
                                                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <UserOutlined style={{ fontSize: 10 }} />
                                                            {msg.operator_name || (msg.operator_id ? operatorsMap[msg.operator_id]?.name : null) || (msg.operator_id ? "Оператор" : (msg.from_user?.first_name || "С телефона"))}
                                                        </div>
                                                    )}
                                                    {!msg.outgoing && !['private', 'bot'].includes(selectedDialog?.chat_type || selectedDialog?.type || '') && (msg.sender_name || msg.from_user?.first_name) && (
                                                        <div style={{ fontSize: 11, color: 'var(--brand-primary)', marginBottom: 4, fontWeight: 600 }}>
                                                            {msg.sender_name || msg.from_user?.first_name}
                                                            {msg.sender_username ? ` (@${msg.sender_username})` : ''}
                                                        </div>
                                                    )}
                                                    {msg.forward_from_name && (
                                                        <div style={{ fontSize: 11, color: msg.outgoing ? 'rgba(255,255,255,0.9)' : 'var(--brand-primary)', marginBottom: 4, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 14 20 9 15 4"></polyline><path d="M4 20v-7a4 4 0 0 1 4-4h12"></path></svg>
                                                            Переслано от: {msg.forward_from_name}
                                                        </div>
                                                    )}
                                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                                        {/* Inline media rendering */}
                                                        {(() => {
                                                            const accId = selectedAccount === -1 ? selectedDialog?.tg_account_id : selectedAccount
                                                            const ct = msg.content_type || 'text'
                                                            if (ct === 'text') {
                                                                if (!msg.text && msg.forward_from_name) {
                                                                    return <span style={{ fontStyle: 'italic', opacity: 0.7 }}>[Пересланное сообщение]</span>
                                                                }
                                                                // Return null so the main text rendering below handles it
                                                                return null
                                                            }
                                                            const mediaUrl = msg.media_file_path 
                                                                ? (msg.media_file_path.startsWith('blob:') 
                                                                    ? msg.media_file_path 
                                                                    : `/api/accounts/${accId}/media/${msg.media_file_path}?token=${localStorage.getItem('token')}`) 
                                                                : undefined
                                                                
                                                            if (!accId || !mediaUrl) return null

                                                            if (ct === 'photo') return (
                                                                <div style={{ position: 'relative', display: 'inline-block', marginBottom: msg.text ? 6 : 0 }}>
                                                                    <Image src={mediaUrl} alt="📷 Фото"
                                                                        style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, display: 'block' }}
                                                                        fallback="https://via.placeholder.com/300x200?text=Ошибка+загрузки+фото"
                                                                        preview={{
                                                                            src: mediaUrl,
                                                                        }}
                                                                    />
                                                                    <a href={mediaUrl} download={msg.media_file_name || 'photo.jpg'} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '6px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Скачать">
                                                                        <DownloadOutlined />
                                                                    </a>
                                                                </div>
                                                            )
                                                            if (ct === 'video' || ct === 'video_note' || ct === 'animation') return (
                                                                <div style={{ marginBottom: msg.text ? 6 : 0 }}>
                                                                    <OnDemandVideo
                                                                        src={mediaUrl}
                                                                        round={ct === 'video_note'}
                                                                        label={ct === 'animation' ? 'GIF' : ct === 'video_note' ? 'видеосообщение' : 'видео'}
                                                                    />
                                                                </div>
                                                            )
                                                            if (ct === 'voice' || ct === 'audio') return (
                                                                <audio src={mediaUrl} controls preload="metadata"
                                                                    style={{ maxWidth: '100%', marginBottom: msg.text ? 6 : 0, display: 'block' }}
                                                                    onError={(e) => { (e.target as HTMLAudioElement).outerHTML = `<span style="font-style:italic;opacity:0.7">${ct === 'voice' ? '🎤 Голосовое' : '🎵 Аудио'} (не удалось загрузить)</span>` }}
                                                                />
                                                            )
                                                            if (ct === 'sticker') return (
                                                                <img src={mediaUrl} alt="Стикер"
                                                                    style={{ maxWidth: 160, maxHeight: 160, display: 'block' }}
                                                                    loading="lazy"
                                                                    onError={(e) => { (e.target as HTMLImageElement).outerHTML = '<span style="font-style:italic;opacity:0.7">🏷 Стикер</span>' }}
                                                                />
                                                            )
                                                            if (ct === 'document') {
                                                                if (msg.media_mime_type?.startsWith('image/')) {
                                                                    return (
                                                                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: msg.text ? 6 : 0 }}>
                                                                            <Image src={mediaUrl} alt="📷 Фото"
                                                                                style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, display: 'block' }}
                                                                                fallback="https://via.placeholder.com/300x200?text=Ошибка+загрузки+фото"
                                                                                preview={{ src: mediaUrl }}
                                                                            />
                                                                            <a href={mediaUrl} download={msg.media_file_name || 'photo.jpg'} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '6px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Скачать">
                                                                                <DownloadOutlined />
                                                                            </a>
                                                                        </div>
                                                                    )
                                                                }
                                                                if (msg.media_mime_type?.startsWith('video/')) {
                                                                    return (
                                                                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: msg.text ? 6 : 0 }}>
                                                                            <OnDemandVideo src={mediaUrl} />
                                                                            <a href={mediaUrl} download={msg.media_file_name || 'video.mp4'} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: 'white', padding: '6px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }} title="Скачать">
                                                                                <DownloadOutlined />
                                                                            </a>
                                                                        </div>
                                                                    )
                                                                }
                                                                if (msg.media_mime_type?.startsWith('audio/')) {
                                                                    return (
                                                                        <audio src={mediaUrl} controls preload="metadata"
                                                                            style={{ maxWidth: '100%', marginBottom: msg.text ? 6 : 0, display: 'block' }}
                                                                            onError={(e) => { (e.target as HTMLAudioElement).outerHTML = `<span style="font-style:italic;opacity:0.7">🎵 Аудио (не удалось загрузить)</span>` }}
                                                                        />
                                                                    )
                                                                }
                                                                return (
                                                                    <a href={mediaUrl} download={msg.media_file_name || 'file'} target="_blank" rel="noopener noreferrer"
                                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.1)', textDecoration: 'none', color: 'inherit', marginBottom: msg.text ? 6 : 0 }}
                                                                    >
                                                                        📎 <span style={{ fontSize: 13, flex: 1, wordBreak: 'break-all' }}>{msg.media_file_name || 'Файл'}</span>
                                                                        <span style={{ padding: '4px 6px', background: 'var(--brand-primary)', color: 'white', borderRadius: 4, display: 'flex', alignItems: 'center' }}>
                                                                            <DownloadOutlined />
                                                                        </span>
                                                                    </a>
                                                                )
                                                            }
                                                            // Fallback for unknown types
                                                            return <span style={{ fontStyle: 'italic', opacity: 0.7 }}>📎 {ct}{msg.media_file_name ? ` (${msg.media_file_name})` : ''}</span>
                                                        })()}
                                                        {safeText(msg.text)}
                                                        {msg.is_edited && (
                                                            <div style={{ fontSize: 10, marginTop: 4, fontStyle: 'italic', color: '#60a5fa', padding: '2px 6px', background: 'rgba(96,165,250,0.1)', borderRadius: 4, display: 'inline-block' }}>
                                                                Отредактировано в Telegram
                                                            </div>
                                                        )}
                                                        {msg.is_deleted_in_telegram && (
                                                            <span style={{ fontSize: 10, display: 'block', fontStyle: 'italic', marginTop: 4 }}>
                                                                (Удалено в Telegram)
                                                            </span>
                                                        )}
                                                    </div>
                                                     <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                                                        <span>
                                                            {msg.is_edited && <span style={{ marginRight: 4, fontStyle: 'italic' }}>изм.</span>}
                                                            {(() => {
                                                                const raw = msg.date || msg.tg_date
                                                                if (!raw) return ''
                                                                const d = new Date(raw)
                                                                if (isNaN(d.getTime())) return ''
                                                                const now = new Date()
                                                                const isToday = d.toDateString() === now.toDateString()
                                                                const yesterday = new Date(now)
                                                                yesterday.setDate(yesterday.getDate() - 1)
                                                                const isYesterday = d.toDateString() === yesterday.toDateString()
                                                                const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                                                if (isToday) return time
                                                                if (isYesterday) return `вчера, ${time}`
                                                                return `${d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}, ${time}`
                                                            })()}
                                                        </span>
                                                        {msg.outgoing && (
                                                            <span style={{ fontSize: 11, color: '#fff' }}>{msg.is_read ? '✓✓' : '✓'}</span>
                                                        )}
                                                     </div>
                                                </div>
                                                )})}
                                        </Fragment>
                                    </div>
                                    {showScrollButton && (
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={<DownOutlined style={{ fontSize: 16 }} />}
                                            onClick={scrollToBottom}
                                            style={{
                                                position: 'absolute', bottom: 16, right: 24,
                                                width: 42, height: 42,
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                                                zIndex: 10,
                                            }}
                                        />
                                    )}
                                </div>

                                <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexDirection: 'column' }}>
                                    {editMessageId && (
                                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, padding: '8px 12px', background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
                                            <div style={{ width: 4, height: 32, background: 'var(--brand-primary)', borderRadius: 2 }} />
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--brand-primary)' }}>Редактирование</div>
                                                <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{newMessage}</div>
                                            </div>
                                            <Button type="text" icon={<CloseOutlined />} onClick={() => { setEditMessageId(null); setNewMessage(''); }} size="small" />
                                        </div>
                                    )}
                                    {/* Reply Banner — как в TG Web */}
                                    {replyTo && !editMessageId && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '8px 12px', margin: '0',
                                            borderLeft: '3px solid #00b894',
                                            background: 'var(--bg-subtle)',
                                        }}>
                                            <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                                                <div style={{ fontSize: 12, color: '#00b894', fontWeight: 600 }}>Ответ</div>
                                                <div style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {replyTo.text || 'Сообщение'}
                                                </div>
                                            </div>
                                            <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)', padding: '4px 8px', flexShrink: 0 }}>✕</button>
                                        </div>
                                    )}
                                    {/* Select Mode Toolbar */}
                                    {isSelectMode && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 12px', background: 'var(--bg-subtle)',
                                        }}>
                                            <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>
                                                Выбрано: {selectedMsgIds.length}
                                            </span>
                                            <Button size="small" danger onClick={async () => {
                                                if (selectedMsgIds.length === 0) return
                                                try {
                                                    const accId = selectedAccount === -1 ? selectedDialog?.tg_account_id : selectedAccount
                                                    const chatId = selectedDialog?.chat_id || selectedDialog?.tg_chat_id || 0
                                                    if (accId && chatId) {
                                                        await accountsApi.deleteMessages(accId, chatId, selectedMsgIds)
                                                        removeMessagesFromLocalState(
                                                            accId,
                                                            [chatId, selectedDialog?.db_chat_id, selectedDialog?.tg_chat_id, selectedDialog?.id],
                                                            selectedMsgIds,
                                                            true,
                                                        )
                                                        void fetchDialogs(false)
                                                        message.success(`Удалено ${selectedMsgIds.length} сообщений`)
                                                    }
                                                } catch (e: any) {
                                                    message.error(e.response?.data?.detail || 'Ошибка удаления')
                                                }
                                                setIsSelectMode(false)
                                                setSelectedMsgIds([])
                                            }}>🗑️ Удалить</Button>
                                            <Button size="small" onClick={() => {
                                                setForwardMsgIds(selectedMsgIds)
                                                setForwardModalOpen(true)
                                                setIsSelectMode(false)
                                                setSelectedMsgIds([])
                                            }}>↗️ Переслать</Button>
                                            <Button size="small" onClick={() => { setIsSelectMode(false); setSelectedMsgIds([]) }}>Отмена</Button>
                                        </div>
                                    )}
                                    {selectedDialog && !conversationReadOnly && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Ответ от:</span>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>
                                                {(() => {
                                                    let targetId = selectedAccount;
                                                    if (selectedAccount === -1) {
                                                        targetId = selectedDialog.tg_account_id ?? null;
                                                    }

                                                    if (!targetId) return "Неизвестный аккаунт";

                                                    const acc = accounts.find(a => a.id === targetId);
                                                    if (!acc) return "Неизвестный аккаунт";
                                                    return acc.username ? `@${acc.username}` : acc.phone;
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 8, position: 'relative', alignItems: 'flex-end' }}>
                                        {conversationReadOnly ? (
                                            <div style={{ flex: 1, padding: 12, textAlign: 'center', color: 'var(--text-muted)' }}>
                                                {activeForumTopic?.is_closed ? 'Топик закрыт' : 'Отправка сообщений недоступна'}
                                            </div>
                                        ) : (
                                            <Fragment>
                                                {/* Emoji picker popup — fixed position to escape overflow:hidden */}
                                                {showEmojiPicker && emojiPos && (
                                                    <div
                                                        ref={emojiPickerRef}
                                                        style={{
                                                            position: 'fixed',
                                                            bottom: window.innerHeight - emojiPos.top,
                                                            left: emojiPos.left,
                                                            background: 'var(--bg-surface)',
                                                            border: '1px solid var(--border)',
                                                            borderRadius: 12,
                                                            padding: 0,
                                                            width: 370,
                                                            maxHeight: 400,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            zIndex: 9999,
                                                            boxShadow: '0 -4px 32px rgba(0,0,0,0.35)',
                                                        }}
                                                    >
                                                        {/* Search */}
                                                        <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--border)' }}>
                                                            <input
                                                                type="text"
                                                                value={emojiSearch}
                                                                onChange={e => setEmojiSearch(e.target.value)}
                                                                placeholder="🔍 Поиск эмодзи..."
                                                                autoFocus
                                                                style={{
                                                                    width: '100%', padding: '8px 12px',
                                                                    border: '1px solid var(--border)', borderRadius: 8,
                                                                    background: 'var(--bg-subtle)', color: 'var(--text-primary)',
                                                                    fontSize: 14, outline: 'none',
                                                                    boxSizing: 'border-box',
                                                                }}
                                                            />
                                                        </div>
                                                        {/* Scrollable content */}
                                                        <div style={{ overflowY: 'auto', padding: '8px 12px 12px', flex: 1 }}>
                                                            {emojiSearch ? (
                                                                /* Search results */
                                                                (() => {
                                                                    const results = searchEmojis(emojiSearch)
                                                                    return results.length > 0 ? (
                                                                        <div>
                                                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>🔍 Результаты ({results.length})</div>
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                                                {results.map(emoji => (
                                                                                    <span key={emoji} onClick={() => handleEmojiSelect(emoji)}
                                                                                        style={{ fontSize: 22, cursor: 'pointer', padding: '2px 4px', borderRadius: 6, transition: 'background 0.15s', lineHeight: 1.3 }}
                                                                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                                                                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                                                    >{emoji}</span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Ничего не найдено</div>
                                                                    )
                                                                })()
                                                            ) : (
                                                                <>
                                                                    {/* Recent */}
                                                                    {recentEmojis.length > 0 && (
                                                                        <div style={{ marginBottom: 12 }}>
                                                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>⏱ Недавние</div>
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                                                {recentEmojis.map((emoji, i) => (
                                                                                    <span key={`recent-${i}`} onClick={() => handleEmojiSelect(emoji)}
                                                                                        style={{ fontSize: 22, cursor: 'pointer', padding: '2px 4px', borderRadius: 6, transition: 'background 0.15s', lineHeight: 1.3 }}
                                                                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                                                                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                                                    >{emoji}</span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {/* Categories */}
                                                                    {EMOJI_CATEGORIES.map(cat => (
                                                                        <div key={cat.name} style={{ marginBottom: 12 }}>
                                                                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat.name}</div>
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                                                {cat.emojis.map(emoji => (
                                                                                    <span key={emoji} onClick={() => handleEmojiSelect(emoji)}
                                                                                        style={{ fontSize: 22, cursor: 'pointer', padding: '2px 4px', borderRadius: 6, transition: 'background 0.15s', lineHeight: 1.3 }}
                                                                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                                                                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                                                    >{emoji}</span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                <input 
                                                    type="file" 
                                                    ref={fileInputRef} 
                                                    style={{ display: 'none' }} 
                                                    onChange={handleFileSelect} 
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        fontSize: 22, padding: '4px 6px', borderRadius: 8,
                                                        color: 'var(--text-muted)',
                                                        transition: 'color 0.15s',
                                                        flexShrink: 0, alignSelf: 'flex-end', marginBottom: 4,
                                                    }}
                                                    title="Прикрепить файл"
                                                >
                                                    <PaperClipOutlined />
                                                </button>
                                                <button
                                                    ref={emojiButtonRef}
                                                    type="button"
                                                    onClick={handleToggleEmoji}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        fontSize: 22, padding: '4px 6px', borderRadius: 8,
                                                        color: showEmojiPicker ? '#0088cc' : 'var(--text-muted)',
                                                        transition: 'color 0.15s',
                                                        flexShrink: 0, alignSelf: 'flex-end', marginBottom: 4,
                                                    }}
                                                    title="Эмодзи"
                                                >🙂</button>
                                                    {mediaFile && (
                                                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, padding: 12, background: 'var(--bg-surface)', borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
                                                            {mediaFile.type.startsWith('image/') ? (
                                                                <img src={URL.createObjectURL(mediaFile)} alt="preview" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                                                            ) : (
                                                                <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', borderRadius: 8, color: '#0088cc', fontSize: 24 }}>
                                                                    <PaperClipOutlined />
                                                                </div>
                                                            )}
                                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mediaFile.name}</div>
                                                                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(mediaFile.size / 1024).toFixed(1)} KB</div>
                                                            </div>
                                                            <Button type="text" danger icon={<CloseOutlined />} onClick={() => setMediaFile(null)} size="small" />
                                                        </div>
                                                    )}
                                                <TextArea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Сообщение..." autoSize={{ minRows: 1, maxRows: 4 }} onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSendMessage(); } }} style={{ flex: 1, borderTopLeftRadius: mediaFile ? 0 : undefined, borderTopRightRadius: mediaFile ? 0 : undefined }} />
                                                <Button type="primary" icon={<SendOutlined />} onClick={handleSendMessage} loading={sending} disabled={!newMessage.trim() && !mediaFile} />
                                            </Fragment>
                                        )}
                                    </div>
                                </div>
                            </Fragment>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Выберите диалог</div>
                        )
                    ) : (
                        selectedInternalChat ? (
                            <Fragment>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Avatar src={selectedInternalChat.other_user.avatar_url}>{(selectedInternalChat.other_user.full_name || 'U')[0]}</Avatar>
                                    <div style={{ fontWeight: 600 }}>{selectedInternalChat.other_user.full_name || selectedInternalChat.other_user.username}</div>
                                </div>
                                <div ref={messagesContainerRef} style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {loadingInternalMessages ? <Spin /> : (
                                        <Fragment>
                                            {internalMessages.map(msg => (
                                                <div key={msg.id} style={{ alignSelf: msg.sender_id === user?.id ? 'flex-end' : 'flex-start', maxWidth: '70%', padding: '10px 14px', borderRadius: 12, background: msg.sender_id === user?.id ? 'var(--brand-primary)' : 'var(--bg-subtle)', color: msg.sender_id === user?.id ? '#fff' : 'var(--text-primary)' }}>
                                                    {msg.text_content}
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </Fragment>
                                    )}
                                </div>
                                <div style={{ padding: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                                    <TextArea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Напишите..." autoSize={{ minRows: 1, maxRows: 4 }} onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSendInternalMessage(); } }} />
                                    <Button type="primary" icon={<SendOutlined />} onClick={handleSendInternalMessage} loading={sending} disabled={!newMessage.trim()} />
                                </div>
                            </Fragment>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Empty description="Выберите чат" /></div>
                        )
                    )}
                </Content>
            </div>

            {/* Контекстное меню сообщения — стиль Telegram Web */}
            {contextMenu && (
                <div 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
                    onClick={() => setContextMenu(null)}
                >
                    <div style={{
                        position: 'fixed',
                        top: contextMenu.y + 360 > window.innerHeight ? window.innerHeight - 360 : contextMenu.y,
                        left: contextMenu.x + 220 > window.innerWidth ? window.innerWidth - 220 : contextMenu.x,
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border)',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                        padding: '6px 0',
                        minWidth: 180,
                        zIndex: 1001
                    }}>
                        {/* Reply */}
                        <div 
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                            onClick={() => {
                                setReplyTo({ id: contextMenu.messageId, text: contextMenu.msgText || '' })
                                setContextMenu(null)
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>↩️</span> Ответить
                        </div>
                        {/* Copy Text */}
                        {contextMenu.msgText && (
                            <div 
                                style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                                onClick={() => {
                                    navigator.clipboard.writeText(contextMenu.msgText || '')
                                    message.success('Текст скопирован')
                                    setContextMenu(null)
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>📋</span> Копировать текст
                            </div>
                        )}
                        {/* Pin / Unpin */}
                        {contextMenu.isPinned ? (
                            <div 
                                style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                                onClick={() => handleUnpinMessage(contextMenu.messageId)}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>📌</span> Открепить
                            </div>
                        ) : (
                            <div 
                                style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                                onClick={() => handlePinMessage(contextMenu.messageId)}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>📌</span> Закрепить
                            </div>
                        )}
                        {/* Forward */}
                        <div 
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                            onClick={() => {
                                setForwardMsgIds([contextMenu.messageId])
                                setForwardModalOpen(true)
                                setContextMenu(null)
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>↗️</span> Переслать
                        </div>
                        {/* Edit message (only for outgoing messages) */}
                        {contextMenu.msgOutgoing && (
                            <div 
                                style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                                onClick={() => {
                                    setEditMessageId(contextMenu.messageId)
                                    setNewMessage(contextMenu.msgText || '')
                                    setContextMenu(null)
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>✏️</span> Изменить
                            </div>
                        )}
                        {/* Select */}
                        <div 
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}
                            onClick={() => {
                                setIsSelectMode(true)
                                setSelectedMsgIds([contextMenu.messageId])
                                setContextMenu(null)
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-subtle)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>☑️</span> Выбрать
                        </div>
                        {/* Separator */}
                        <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
                        {/* Delete for me */}
                        <div 
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 12 }}
                            onClick={async () => {
                                const msgId = contextMenu.messageId
                                setContextMenu(null)
                                try {
                                    const accId = selectedAccount === -1 ? selectedDialog?.tg_account_id : selectedAccount
                                    const chatId = selectedDialog?.chat_id || selectedDialog?.tg_chat_id || 0
                                    if (accId && chatId) {
                                        await accountsApi.deleteMessages(accId, chatId, [msgId], false)
                                        removeMessagesFromLocalState(
                                            accId,
                                            [chatId, selectedDialog?.db_chat_id, selectedDialog?.tg_chat_id, selectedDialog?.id],
                                            [msgId],
                                            true,
                                        )
                                        void fetchDialogs(false)
                                        message.success('Удалено у себя')
                                    }
                                } catch (e: any) {
                                    message.error(e.response?.data?.detail || 'Ошибка удаления')
                                }
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>🗑️</span> Удалить у себя
                        </div>
                        {/* Delete for everyone */}
                        <div 
                            style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 12 }}
                            onClick={async () => {
                                const msgId = contextMenu.messageId
                                setContextMenu(null)
                                try {
                                    const accId = selectedAccount === -1 ? selectedDialog?.tg_account_id : selectedAccount
                                    const chatId = selectedDialog?.chat_id || selectedDialog?.tg_chat_id || 0
                                    if (accId && chatId) {
                                        await accountsApi.deleteMessages(accId, chatId, [msgId], true)
                                        removeMessagesFromLocalState(
                                            accId,
                                            [chatId, selectedDialog?.db_chat_id, selectedDialog?.tg_chat_id, selectedDialog?.id],
                                            [msgId],
                                            true,
                                        )
                                        void fetchDialogs(false)
                                        message.success('Удалено у всех')
                                    }
                                } catch (e: any) {
                                    message.error(e.response?.data?.detail || 'Ошибка удаления')
                                }
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>🗑️</span> Удалить у всех
                        </div>
                    </div>
                </div>
            )}

            {/* Forward Modal — выбор чата для пересылки */}
            <Modal
                title="Переслать сообщение"
                open={forwardModalOpen}
                onCancel={() => { setForwardModalOpen(false); setForwardTargetChatIds([]); setForwardSearchQuery(''); }}
                onOk={async () => {
                    if (forwardTargetChatIds.length === 0 || forwardMsgIds.length === 0) return
                    try {
                        const accId = selectedAccount === -1 ? selectedDialog?.tg_account_id : selectedAccount
                        const fromChatId = selectedDialog?.chat_id || selectedDialog?.tg_chat_id || 0
                        if (accId && fromChatId) {
                            await accountsApi.forwardMessages(accId, fromChatId, forwardTargetChatIds, forwardMsgIds)
                            message.success('Сообщение переслано')
                        }
                    } catch (e: any) {
                        message.error(e.response?.data?.detail || 'Ошибка пересылки')
                    }
                    setForwardModalOpen(false)
                    setForwardTargetChatIds([])
                    setForwardSearchQuery('')
                    setForwardMsgIds([])
                }}
                okText="Переслать"
                cancelText="Отмена"
                okButtonProps={{ disabled: forwardTargetChatIds.length === 0 }}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Поиск кому переслать..."
                        prefix={<SearchOutlined />}
                        value={forwardSearchQuery}
                        onChange={(e) => setForwardSearchQuery(e.target.value)}
                        allowClear
                    />
                </div>
                <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>Выберите чат(ы):</p>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                    {dialogs.filter(d => {
                        if ((d.chat_id || d.tg_chat_id) === (selectedDialog?.chat_id || selectedDialog?.tg_chat_id)) return false;
                        if (!forwardSearchQuery) return true;
                        const query = forwardSearchQuery.toLowerCase();
                        return (d.first_name || '').toLowerCase().includes(query) ||
                               (d.last_name || '').toLowerCase().includes(query) ||
                               (d.username || '').toLowerCase().includes(query) ||
                               ((d as any).phone || '').toLowerCase().includes(query);
                    }).map(d => {
                        const chatId = d.chat_id || d.tg_chat_id || 0;
                        const isSelected = forwardTargetChatIds.includes(chatId);
                        return (
                            <div
                                key={chatId}
                                onClick={() => {
                                    setForwardTargetChatIds(prev => 
                                        prev.includes(chatId) ? prev.filter(id => id !== chatId) : [...prev, chatId]
                                    )
                                }}
                                style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    background: isSelected ? 'rgba(0,184,148,0.1)' : 'transparent',
                                }}
                            >
                                <div style={{ 
                                    width: 18, 
                                    height: 18, 
                                    borderRadius: 4, 
                                    border: isSelected ? 'none' : '2px solid var(--text-muted)', 
                                    background: isSelected ? 'var(--brand-primary)' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {isSelected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                                </div>
                                <Avatar src={d.photo_url || d.contact_photo_url || undefined} icon={<UserOutlined />} size={32} style={{ background: '#0088cc' }} />
                                <span style={{ color: 'var(--text-primary)' }}>{d.first_name || d.username || 'Unknown'}{d.last_name ? ` ${d.last_name}` : ''}</span>
                            </div>
                        );
                    })}
                </div>
            </Modal>

            <Modal title="Новый чат" open={newChatModalOpen} onCancel={() => { setNewChatModalOpen(false); setSearchResults([]); setSelectedSearchResult(null); }} footer={null}>
                <Radio.Group value={newChatType} onChange={(e) => { setNewChatType(e.target.value); setSearchResults([]); setSelectedSearchResult(null); }} style={{ marginBottom: 16 }}>
                    <Radio.Button value="username">Username</Radio.Button>
                    <Radio.Button value="phone">Телефон</Radio.Button>
                </Radio.Group>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <Input
                        placeholder={newChatType === 'username' ? '@username' : '+7XXXXXXXXXX'}
                        value={newChatTarget}
                        onChange={(e) => { setNewChatTarget(e.target.value); setSearchResults([]); setSelectedSearchResult(null); }}
                        onPressEnter={handleSearchUser}
                        style={{ flex: 1 }}
                    />
                    <Button icon={<SearchOutlined />} onClick={handleSearchUser} loading={searchingUser}>
                        Найти
                    </Button>
                </div>
                {searchResults.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
                        {searchResults.map(user => (
                            <div 
                                key={user.id}
                                onClick={() => {
                                    setSelectedSearchResult(user)
                                    setNewChatTarget(user.username || user.phone || String(user.id))
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: selectedSearchResult?.id === user.id ? 'var(--brand-primary)' : 'var(--bg-subtle)', color: selectedSearchResult?.id === user.id ? '#fff' : 'inherit', borderRadius: 8, cursor: 'pointer' }}
                            >
                                <Avatar
                                    src={user.photo_url || undefined}
                                    icon={!user.photo_url ? <UserOutlined /> : undefined}
                                    size={40}
                                    style={{ background: '#0088cc' }}
                                />
                                <div>
                                    <div style={{ fontWeight: 600 }}>
                                        {user.first_name || ''} {user.last_name || ''}
                                    </div>
                                    {user.username && (
                                        <div style={{ fontSize: 12, opacity: 0.8 }}>@{user.username}</div>
                                    )}
                                    {user.is_bot && <Tag color={selectedSearchResult?.id === user.id ? "white" : "blue"} style={{ marginTop: 4, color: selectedSearchResult?.id === user.id ? "#0088cc" : undefined }}>Бот</Tag>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <TextArea placeholder="Сообщение..." value={newChatMessage} onChange={(e) => setNewChatMessage(e.target.value)} rows={3} style={{ marginBottom: 16 }} />
                <Button type="primary" block onClick={handleSendNewChat} loading={sendingNewChat} disabled={!newChatTarget.trim() || !newChatMessage.trim()}>Отправить</Button>
            </Modal>

            <Modal title="Продолжить диалог" open={continueChatModalOpen} onCancel={() => setContinueChatModalOpen(false)} onOk={handleContinueChat} confirmLoading={continuingChat}>
                <Select style={{ width: '100%' }} placeholder="Аккаунт" value={continueTargetAccount} onChange={setContinueTargetAccount}>
                    {accounts.filter(acc => acc.status === 'active').map(acc => <Select.Option key={acc.id} value={acc.id}>{acc.username || acc.phone}</Select.Option>)}
                </Select>
            </Modal>

            <Modal title="Удалить чат" open={deleteChatModalOpen} onCancel={() => setDeleteChatModalOpen(false)} footer={null}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <p style={{ margin: 0, marginBottom: 8 }}>Выберите вариант удаления чата:</p>
                    <Button type="default" danger onClick={() => handleDeleteChat(false)} style={{ textAlign: 'left', padding: '8px 16px', height: 'auto' }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>Удалить только из TG Manager</div>
                            <div style={{ fontSize: 12, opacity: 0.8 }}>Чат скроется здесь, но останется в Telegram</div>
                        </div>
                    </Button>
                    <Button type="primary" danger onClick={() => handleDeleteChat(true)} style={{ textAlign: 'left', padding: '8px 16px', height: 'auto' }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>Удалить везде</div>
                            <div style={{ fontSize: 12, opacity: 0.9 }}>Чат будет удалён и здесь, и в Telegram</div>
                        </div>
                    </Button>
                </div>
            </Modal>

        </div>
    )
}

export default Chats
